import React from 'react'
import Layout from 'components/app/layout'
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import HeroPlanetImage from '../../../../public/assets/conf/conf-hero.jpg'
import {ChevronRightIcon, PlayIcon} from '@heroicons/react/solid'
import {track} from 'utils/analytics'
import {DocumentIcon, StarIcon} from '@heroicons/react/outline'
import {type Talk} from 'lib/talks'
import {useMeasure} from 'react-use'
import {cn} from '@skillrecordings/ui/utils/cn'
import MuxPlayer from '@mux/mux-player-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@skillrecordings/ui'
import {DialogTrigger} from '@radix-ui/react-dialog'
import {GetStaticProps} from 'next'
import {shuffle} from 'lodash'
import {format, parseISO} from 'date-fns'
// import {TwitterIcon, BlueskyIcon} from '@skillrecordings/react'
import Icon from 'components/icons'

import {useRouter} from 'next/router'
import slugify from '@sindresorhus/slugify'
import {trpc} from 'trpc/trpc.client'

export const IS_PAST_CONF_25 = false
export const CONF_25_TITO_URL = 'https://ti.to/epicweb/epicweb-conf-2025'
const CONF_25_SESSIONIZE_URL = 'https://sessionize.com/epicweb-conf-2025/'
const HOTEL_PROMO_CODE = 'W14'

const CK_CONF_2025_FIELD = {
  [`conf_2025`]: new Date().toISOString().slice(0, 10),
}
// const HOTEL_PROMO_CODE = 'W14'

export type Speaker = {
  video?: {
    _id: string
    _type: 'tip'
    slug: string
    muxPlaybackId: string
    transcript: {text: string}
    videoResourceId: string
    poster: string
  }
  id: string
  bio: string
  firstName: string
  sessions: {
    id: string
    name: string
  }[]
  lastName: string
  fullName: string
  tagLine: string
  profilePicture: string
  isTopSpeaker: boolean
}

type Session = {
  id: string
  title: string
  description: string
  startsAt: string
  endsAt: string
  speakers: {name: string; id: string}[]
  talk?: Talk | null
}

type Room = {
  id: number
  name: string
  sessions: Session[]
}

type Day = {
  date: string
  rooms: Room[]
}

export type Schedule = Day[]

export const getStaticProps: GetStaticProps = async () => {
  const speakers = await fetch(
    'https://sessionize.com/api/v2/wyvikoxy/view/Speakers',
  ).then((res) => res.json())
  const schedule = await fetch(
    'https://sessionize.com/api/v2/wyvikoxy/view/GridSmart',
  ).then((res) => res.json())

  return {
    props: {speakers: speakers, schedule: schedule},
    revalidate: 60 * 5,
  }
}

const ConfPage: React.FC<{speakers: Speaker[]; schedule: Schedule}> = ({
  speakers,
  schedule,
}) => {
  const {data: livestreamData, status: livestreamStatus} =
    trpc.conf.livestream.useQuery()

  return (
    <Layout
      className="bg-foreground pt-16 text-background dark:bg-background dark:text-foreground"
      meta={{
        title: 'Epic Web Conf 2025',
        titleAppendSiteName: false,
        description:
          'The Full Stack Web Development Conference of Epic proportions.',
        ogImage: {
          url: 'https://res.cloudinary.com/epic-web/image/upload/v1731634239/ogImage-2025conf.jpg',
        },
      }}
    >
      {/* <EventJsonLd
        name="Epic Web Conf 2025"
        startDate="2025-04-10T08:00:00-07:00"
        endDate="2025-04-11T17:00:00-07:00"
        location={{
          name: 'Hilton Salt Lake City Center',
          address: {
            streetAddress: '2175 Sidewinder Dr',
            addressLocality: 'Park City',
            postalCode: '84060',
            addressRegion: 'UT',
            addressCountry: 'US',
          },
        }}
        offers={[
          {
            seller: {name: 'epicweb.dev'},
            price: '300.00',
            priceCurrency: 'USD',
            url: CONF_24_TITO_URL,
            validFrom: '2024-01-01T08:00',
          },
        ]}
        performer={[
          {
            name: 'Epic Web',
            url: 'https://epic-web.dev',
          },
        ]}
        image="https://res.cloudinary.com/epic-web/image/upload/v1705997895/conf-card_2x.jpg"
        description="The Full Stack Web Development Conference of Epic proportions."
      /> */}
      <TicketsMarquee />
      <Header />
      {livestreamData?.showLivestream ? (
        <div className="relative z-10 mx-auto mb-16  w-full max-w-screen-lg px-5 pt-12 sm:-mt-16">
          <iframe
            src="https://www.youtube.com/embed/SDuvi5eUqp0?si=dzNuvYGzmwzUq0iC"
            title="YouTube video player"
            className="aspect-video w-full rounded border border-white/5"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      ) : null}
      <Body speakers={speakers} schedule={schedule} />

      <Footer />
    </Layout>
  )
}

export default ConfPage

const Body = ({
  speakers,
  schedule,
}: {
  speakers: Speaker[]
  schedule: Schedule
}) => {
  let epicTalkIdeas = [
    'Blow our minds with cool demos',
    'Show off what you’ve learned at scale',
    'Help us be better professionals, team mates, and community members',
    'Help us find a job or build our own company',
    'Show us how you made something fast',
    'Inspire us to do more with AI than build a chatbot',
  ]

  const Section = ({
    title,
    children,
    cta,
    position = 0,
    image,
  }: {
    title: string
    children: React.ReactNode
    position?: number
    image?: string
    cta?: {
      href: string
      label: React.ReactNode
      track?: string
    }
  }) => {
    return (
      <section aria-label={title} className="relative px-5">
        <div
          className={cn('flex flex-col items-start sm:flex-row sm:gap-16', {
            'sm:flex-row-reverse': position % 2 === 1,
          })}
        >
          {image && (
            <div className="mx-auto flex w-full flex-shrink-0 items-center sm:mx-0 sm:max-w-[400px]">
              <Image
                src={image}
                width={500}
                height={500}
                quality={100}
                className="rounded-lg"
                alt=""
                aria-hidden="true"
              />
            </div>
          )}
          <div className="-mt-10 sm:mt-0">
            <h2 className="mb-6 text-balance text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {title}
            </h2>
            <div className="prose prose-invert w-full max-w-none sm:prose-lg prose-p:text-[#D6DEFF] prose-li:text-[#D6DEFF] sm:prose-p:text-balance">
              {children}
            </div>
            {cta && (
              <Button
                asChild
                className="group relative mt-8 inline-flex h-12 items-center justify-center overflow-hidden border border-white/10 bg-white/10 text-base font-medium shadow-soft-lg transition duration-300 ease-in-out"
                size="lg"
              >
                <Link
                  href={cta.href}
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={() => {
                    track(`clicked ${cta.track || cta.label}`, {
                      title: 'conf2025',
                      type: 'event',
                      location: 'body',
                    })
                  }}
                >
                  {cta.label}
                  <ChevronRightIcon className="-mr-2 ml-2 w-4 text-gray-500 transition duration-300 ease-in-out group-hover:text-white" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </section>
    )
  }

  const promoVideo = 'deoAaA7OUZDPrLjXc013MQaTcTwC9kAY3Pmf2JmF01TOs'
  const [showingSpeakerDetail, setShowingSpeakerDetail] = React.useState<
    boolean | Speaker
  >(false)
  const router = useRouter()
  const [shuffledSpeakers, setShuffledSpeakers] = React.useState<Speaker[]>([])
  React.useEffect(() => {
    setShuffledSpeakers(shuffle(speakers))
  }, [])
  return (
    <div className="mx-auto flex w-full max-w-screen-lg flex-col gap-16 sm:gap-16">
      <Schedule schedule={schedule} speakers={speakers} />
      <SpeakersList
        speakers={shuffledSpeakers}
        showingSpeakerDetail={showingSpeakerDetail}
        setShowingSpeakerDetail={setShowingSpeakerDetail}
      />
      <div className="px-5">
        <MuxPlayer
          playbackId={promoVideo}
          className="w-full rounded shadow-xl"
          accentColor="#3b82f6"
          poster={`https://image.mux.com/${promoVideo}/thumbnail.jpg?time=3`}
          metadata={{
            video_title: 'Epic Web Conf 2025 Promo Video',
          }}
        />
      </div>

      <Location />
      <WebDevChallengeLocation />
      <Section
        position={1}
        title="Join us in March 2025"
        image="https://res.cloudinary.com/epic-web/image/upload/v1728471924/conf25/attendee_2x.jpg"
        cta={{href: CONF_25_TITO_URL, label: 'Buy Tickets'}}
      >
        <p>
          Epic Web Conf is your opportunity to join other full stack web
          developers from all over the world to collaborate on the present and
          future state of the art of building the absolute best user and
          developer experiences possible.
        </p>
        <p>
          Right now we are offering early bird tickets, this is{' '}
          <strong>the cheapest</strong> the price will be and the price goes up
          soon.
        </p>
      </Section>
      <Section
        position={2}
        title="Become a Sponsor"
        image="https://res.cloudinary.com/epic-web/image/upload/v1728472065/conf25/sponsors_2x.jpg"
        cta={{
          href: 'mailto:conf@epicweb.dev?subject=Sponsoring Epic Web Conf 2025',
          label: 'Sponsor Epic Web Conf 2025',
          track: 'become a sponsor',
        }}
      >
        <p>Epic Web Conf is made possible by the support of our sponsors.</p>
        <p>
          <Link href="/conf/2024" target="_blank">
            Last year
          </Link>{' '}
          was EPIC and we'd love to parter with you to make 2025 even better.
        </p>
      </Section>

      {/* <Section
        position={0}
        title="Become a Speaker"
        image="https://res.cloudinary.com/epic-web/image/upload/v1728472122/conf25/speaker_2x.jpg"
        cta={{href: CONF_25_SESSIONIZE_URL, label: 'Submit a Talk'}}
      >
        <p>
          We want to see how you are making the web{' '}
          <span className="font-bold">EPIC</span>. This conference is about
          inspiring us to do and be more.
        </p>
        <p>
          Here are some general categories of what would make a great talk at
          Epic Web Conf 2025:
        </p>
        <ol className=" ">
          {epicTalkIdeas.map((idea, index) => (
            <li key={index}>{idea}</li>
          ))}
        </ol>
      </Section> */}
      {!IS_PAST_CONF_25 && <HotelSection />}
      <Sponsors />
    </div>
  )
}

const Header = () => {
  const {data: livestreamData, status: livestreamStatus} =
    trpc.conf.livestream.useQuery()

  return (
    <header className="relative flex h-full w-full items-center justify-center overflow-hidden bg-[#080B16]">
      <div className="relative z-10 mx-auto w-full max-w-screen-lg px-5 pb-10 pt-16 sm:pb-16 sm:pt-40">
        <h1 className="max-w-xl text-balance text-4xl font-bold text-white sm:text-5xl lg:text-6xl">
          <span className="font-normal">Epic Web</span> Conference 2025
        </h1>
        <h2 className="max-w-sm pt-5 text-xl text-[#93A1D7] lg:max-w-full">
          The Full Stack Web Development Conference of Epic proportions
        </h2>
        <hr className="relative z-0 mb-5 mt-12 max-w-[200px] border-[#202537] sm:max-w-lg lg:max-w-xl" />
        <div className="grid max-w-sm grid-cols-1 items-center gap-5 sm:grid-cols-2 lg:flex lg:max-w-lg lg:gap-14">
          <div>
            <div className="font-mono text-sm uppercase tracking-wide text-[#93A1D7]">
              Conference Day
            </div>
            <div className="text-lg text-[#D6DEFF]">March 26, 2025</div>
          </div>
          <div>
            <div className="font-mono text-sm uppercase tracking-wide text-[#93A1D7]">
              Workshop Day
            </div>
            <div className="text-lg text-[#D6DEFF]">March 25, 2025</div>
          </div>
          <div>
            <div className="font-mono text-sm uppercase tracking-wide text-[#93A1D7]">
              Location
            </div>
            <div className="text-lg text-[#D6DEFF]">Salt Lake City, Utah</div>
          </div>
        </div>
        {/* <Button
          asChild
          className="relative mt-10 h-12 overflow-hidden rounded-sm bg-gradient-to-b from-amber-500 to-amber-600 text-base font-semibold tracking-tight text-amber-950 shadow-lg shadow-amber-500/20 brightness-125 transition duration-300 hover:brightness-110"
          size="lg"
        >
          {CONF_25_TITO_URL && (
            <Link
              href={CONF_25_TITO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track('clicked buy early bird tickets', {
                  title: 'conf2025',
                  type: 'event',
                  location: 'top',
                })
              }}
            >
              Buy Tickets
              <div
                className="absolute left-0 top-0 h-full w-full"
                aria-hidden="true"
              >
                <motion.div
                  className="absolute left-0 top-0 h-full w-5 -skew-x-12 bg-white/20 blur-sm"
                  animate={{left: ['-10%', '110%'], opacity: [0, 1, 0]}}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    // type: 'spring',
                    // make it feel like racing stripe
                    ease: 'circInOut',
                  }}
                />
              </div>
            </Link>
          )}
        </Button> */}
        {/* {livestreamData?.showLivestream ? (
          <Button
            asChild
            className="h-12 gap-2 rounded-sm bg-gradient-to-b from-red-500 to-red-600 font-mono text-base font-bold uppercase tracking-wide text-white transition hover:brightness-110"
            size="lg"
          >
            <Link
              href={livestreamData.livestreamUrl}
              target="_blank"
              onClick={() => {
                track('clicked watch live', {
                  title: 'conf2025',
                  type: 'event',
                  location: 'top',
                })
              }}
            >
              <Icon name="Playmark" className="w-3" />
              {'Watch Live'}
            </Link>
          </Button>
        ) : null} */}
        <Button
          asChild
          className="mt-10 h-12 rounded-sm bg-gradient-to-b from-[#50BBFF] to-[#6397FF] font-mono text-base font-bold uppercase tracking-wide text-gray-950 transition hover:brightness-110"
          size="lg"
        >
          <Link
            href="/talks"
            onClick={() => {
              track('clicked watch recordings', {
                title: 'conf2024',
                type: 'event',
                location: 'top',
              })
            }}
          >
            Watch Recordings
          </Link>
        </Button>
      </div>
      <div className="absolute -bottom-16 right-[-370px] flex items-center justify-center sm:bottom-auto sm:right-[-690px] xl:right-[-600px] 2xl:right-[-370px]">
        <Image
          priority
          src={HeroPlanetImage}
          alt=""
          aria-hidden="true"
          quality={100}
          width={1561}
          height={842}
        />
        <Image
          priority
          src={require('../../../../public/assets/conf/ship@2x.png')}
          alt=""
          aria-hidden="true"
          quality={100}
          width={326}
          height={209}
          className="absolute z-10 w-48 -translate-x-24 translate-y-20 sm:w-auto sm:-translate-x-48 sm:translate-y-28"
        />
      </div>
      <div
        className="absolute bottom-0 left-0 flex h-20 w-full bg-gradient-to-t from-foreground to-transparent dark:from-background"
        aria-hidden="true"
      />
    </header>
  )
}

const Footer = () => {
  return (
    <section className="flex flex-col items-center justify-center pt-16 sm:pt-16">
      <Button
        asChild
        className="relative z-20 mx-auto h-12 overflow-hidden rounded-sm bg-gradient-to-b from-amber-500 to-amber-600 text-base font-semibold tracking-tight text-amber-950 shadow-lg shadow-amber-500/20 brightness-125 transition duration-300 hover:brightness-110"
        size="lg"
      >
        {CONF_25_TITO_URL && (
          <Link
            href={CONF_25_TITO_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track('clicked buy early bird tickets', {
                title: 'conf2025',
                type: 'event',
                location: 'top',
              })
            }}
          >
            Buy Tickets
            <div
              className="absolute left-0 top-0 h-full w-full"
              aria-hidden="true"
            >
              <motion.div
                className="absolute left-0 top-0 h-full w-5 -skew-x-12 bg-white/20 blur-sm"
                animate={{left: ['-10%', '110%'], opacity: [0, 1, 0]}}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  // type: 'spring',
                  // make it feel like racing stripe
                  ease: 'circInOut',
                }}
              />
            </div>
          </Link>
        )}
      </Button>
      {/* <Link
        href="/conf/code-of-conduct"
        className="inline-flex w-full items-center justify-center gap-1 text-center text-[#93A1D7] transition hover:brightness-125"
      >
        <DocumentIcon
          className="w-4"
          stroke="currentColor"
          aria-hidden="true"
        />
        Code of Conduct
      </Link> */}
      <div className="relative flex w-full flex-col items-center justify-center overflow-hidden px-5 sm:-mt-16 sm:pb-16 lg:-mt-24">
        <div className="relative z-10 mx-auto flex h-[240px] w-full max-w-screen-lg flex-col items-center justify-center sm:h-[320px]">
          <Image
            loading="eager"
            src={require('../../../../public/assets/conf/big-planet-bottom@2x.png')}
            alt=""
            aria-hidden="true"
            className="absolute bottom-0 sm:bottom-auto"
            quality={100}
          />
          <Image
            loading="eager"
            width={153}
            height={102}
            src={require('../../../../public/assets/conf/ship2@2x.png')}
            alt=""
            aria-hidden="true"
            className="absolute bottom-24 translate-x-96"
            quality={100}
          />
          <Image
            loading="eager"
            width={255}
            height={170}
            src={require('../../../../public/assets/conf/ship3@2x.png')}
            alt=""
            aria-hidden="true"
            className="absolute bottom-0 -translate-x-96"
            quality={100}
          />
          {/* <div
            className="absolute bottom-0 left-0 z-10 h-px w-full bg-gradient-to-r from-transparent via-cyan-300 to-transparent opacity-20"
            aria-hidden="true"
          /> */}
        </div>
      </div>
    </section>
  )
}

const getProfilePictureForWorkshopInstructor = (
  name: string,
  speakers: Speaker[],
) => {
  const speaker = speakers.find((s) => s.fullName === name)
  return speaker?.profilePicture as string
}

const TicketsMarquee = () => {
  const GAP = 8

  const [textRef, {x, y, width: textWidth, height, top, right, bottom, left}] =
    useMeasure()

  const shouldReduceMotion = useReducedMotion()

  return (
    <Link
      href={CONF_25_TITO_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        track('clicked early bird marquee', {
          title: 'conf2025',
          type: 'event',
          location: 'top',
        })
      }}
      aria-hidden="true"
      className="absolute top-12 z-20 flex w-full items-center justify-center overflow-hidden bg-gray-800 sm:top-12"
    >
      <motion.div
        className=""
        animate={!shouldReduceMotion && {x: [0, -(GAP + textWidth)]}}
        transition={{
          repeat: Infinity,
          duration: 5,
          repeatType: 'loop',
          ease: 'linear',
        }}
      >
        <div
          className={`pointer-events-none flex justify-center space-x-2 py-2 text-xs font-semibold uppercase text-gray-300`}
        >
          {new Array(20).fill('Get Tickets').map((text, index) => (
            <div
              ref={textRef as any}
              className="flex flex-shrink-0 items-center gap-2"
              key={index}
            >
              {text}
              <span className="text-gray-500">✦</span>
            </div>
          ))}
        </div>
      </motion.div>
    </Link>
  )
}

const Location = () => {
  return (
    <section className="mx-auto flex w-full max-w-screen-lg flex-col items-center justify-center px-5 pb-16">
      <h2 className="w-fit self-start pb-5 text-3xl font-semibold sm:text-4xl">
        Location
      </h2>
      <div className="flex flex-col items-center rounded border border-[#313646] bg-[#1E212C] lg:flex-row">
        <div className="flex max-w-md flex-col gap-5 py-6 pl-6 pr-6 sm:py-8 sm:pl-10 sm:pr-0 lg:py-14 lg:pl-16 [&_p]:text-lg [&_p]:leading-relaxed [&_p]:text-[#D6DEFF]">
          <svg
            className=""
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.75 8.5C16.75 13.75 10 18.25 10 18.25C10 18.25 3.25 13.75 3.25 8.5C3.25 6.70979 3.96116 4.9929 5.22703 3.72703C6.4929 2.46116 8.20979 1.75 10 1.75C11.7902 1.75 13.5071 2.46116 14.773 3.72703C16.0388 4.9929 16.75 6.70979 16.75 8.5Z"
              stroke="#3FACFF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 10.75C11.2426 10.75 12.25 9.74264 12.25 8.5C12.25 7.25736 11.2426 6.25 10 6.25C8.75736 6.25 7.75 7.25736 7.75 8.5C7.75 9.74264 8.75736 10.75 10 10.75Z"
              stroke="#3FACFF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p>
            The event is set in the heart of{' '}
            <strong>
              <Link
                href="https://maps.app.goo.gl/dpCVp2TtT9iNf6FKA"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Salt Lake City, Utah
              </Link>
            </strong>
            {
              ', and the free livestream will be available to reach even the most distant Epic web developers.'
            }
          </p>
          <p>
            You'll want to be here in Salt Lake City to rub shoulders with some
            of the best developers working on the web, just like you.
          </p>
        </div>
        <div className="relative flex flex-col items-center justify-center">
          <div className="relative">
            <div
              className="absolute left-0 top-0 hidden h-full w-56 bg-gradient-to-r from-[#1E212C] to-transparent lg:block"
              aria-hidden="true"
            />
            <div
              className="absolute left-0 top-0 block h-40 w-full bg-gradient-to-b from-[#1E212C] to-transparent lg:hidden"
              aria-hidden="true"
            />
            <Image
              width={554}
              height={424}
              src={require('../../../../public/assets/conf/map-venue-2025.png')}
              loading="eager"
              alt="Map showing Hilton Salt Lake City Center location"
              quality={100}
            />
          </div>
          <Link
            href="https://book.passkey.com/event/50932723/owner/1422/home"
            target="_blank"
            onClick={() => {
              track('clicked venue', {
                title: 'conf2025',
                type: 'venue',
                location: 'map',
              })
            }}
            rel="noopener noreferrer"
            className="group absolute -bottom-16 flex scale-[0.8] items-center justify-center rounded bg-white text-gray-900 before:absolute before:-top-1.5 before:-ml-7 before:h-3 before:w-3 before:rotate-45 before:bg-white before:content-[''] sm:-bottom-10 sm:scale-100"
          >
            <div className="overflow-hidden rounded-l">
              <Image
                src={require('../../../../public/assets/conf/hilton-hotel.png')}
                alt="Hilton Salt Lake City Center"
                width={152}
                height={233}
                loading="eager"
                className="transition duration-300 ease-in-out group-hover:scale-105"
              />
            </div>
            <div className="px-4 py-2 pr-5 sm:px-5 sm:py-5 sm:pr-7">
              <h3 className="text-lg font-semibold leading-tight sm:text-xl sm:leading-tight">
                Hilton Salt Lake City Center
              </h3>
              <div className="mt-3 inline-flex text-sm hover:underline">
                255 S W Temple St
                <br />
                Salt Lake City, UT 84101
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  )
}

const Sponsors = () => {
  const {platinum, gold, silver, community} = sponsorsData
  return (
    <section
      id="sponsors"
      aria-label="sponsors"
      className="mx-auto flex w-full max-w-screen-xl scale-90 flex-col items-center justify-center gap-10 pb-10 sm:pb-16 lg:scale-100 [&_p]:text-[#93A1D7]"
    >
      <h2 className="pb-5 text-3xl font-semibold sm:text-4xl">Sponsors</h2>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-16">
        {platinum.length > 0 && (
          <div className="flex w-full flex-col items-center gap-10 px-5 sm:flex-col sm:px-10">
            <div className="flex w-full items-center justify-center border-b py-2 sm:justify-start">
              <p className="font-mono text-sm uppercase">Platinum Sponsors</p>
            </div>
            <div className="flex flex-col flex-wrap items-center gap-10 sm:flex-row sm:gap-20">
              {platinum.map(
                (s: {name: string; url: string; logo: React.JSX.Element}) => {
                  return (
                    <Link
                      href={s.url}
                      onClick={() => {
                        track('clicked platinum sponsor', {
                          title: 'conf2025',
                          type: 'sponsor',
                          location: s.name,
                        })
                      }}
                      key={s.name}
                      className="flex items-center justify-center opacity-90 transition hover:opacity-100"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div className="not-prose">{s.logo}</div>
                    </Link>
                  )
                },
              )}
            </div>
          </div>
        )}
        {gold.length > 0 && (
          <div className="flex w-full flex-col items-center gap-10 px-5 sm:flex-col sm:px-10">
            <div className="flex w-full items-center justify-center border-b py-2 sm:justify-start">
              <p className="font-mono text-sm uppercase">Gold Sponsors</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-10 sm:gap-16">
              {gold.map((s) => {
                return (
                  <Link
                    href={s.url}
                    onClick={() => {
                      track('clicked gold sponsor', {
                        title: 'conf2025',
                        type: 'sponsor',
                        location: s.name,
                      })
                    }}
                    key={s.name}
                    className="not-prose flex items-center justify-center opacity-90 transition hover:opacity-100"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div>{s.logo}</div>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
        {silver.length > 0 && (
          <div className="flex w-full flex-col items-center gap-10 px-5 sm:flex-col sm:px-10">
            <div className="flex w-full items-center justify-center border-b py-2 sm:justify-start">
              <p className="font-mono text-sm uppercase">Silver Sponsors</p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-10 md:justify-start lg:gap-16">
              {silver.map(
                (s: {name: string; url: string; logo: React.JSX.Element}) => {
                  return (
                    <Link
                      href={s.url}
                      key={s.name}
                      onClick={() => {
                        track('clicked silver sponsor', {
                          title: 'conf2025',
                          type: 'sponsor',
                          location: s.name,
                        })
                      }}
                      className="flex items-center justify-center opacity-90 transition hover:opacity-100"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div>{s.logo}</div>
                    </Link>
                  )
                },
              )}
            </div>
          </div>
        )}
        {community.length > 0 && (
          <div className="flex flex-col items-center gap-5 px-5 sm:flex-col sm:px-10">
            <div className="flex w-full items-center justify-center border-b py-2 sm:justify-start">
              <p className="font-mono text-sm uppercase">Community Sponsors</p>
            </div>
            <div className="flex max-w-4xl flex-wrap items-center justify-center gap-8 sm:gap-x-16 sm:gap-y-5">
              {community.map(
                (s: {name: string; url: string; logo: React.JSX.Element}) => {
                  return (
                    <Link
                      href={s.url}
                      key={s.name}
                      onClick={() => {
                        track('clicked community sponsor', {
                          title: 'conf2025',
                          type: 'sponsor',
                          location: s.name,
                        })
                      }}
                      className="flex items-center justify-center opacity-90 transition hover:opacity-100"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <div>{s.logo}</div>
                    </Link>
                  )
                },
              )}
            </div>
          </div>
        )}
      </div>
      {/* <div className="relative mt-5 flex w-full flex-col items-center justify-center gap-10 pb-12 pt-5 sm:flex-row">
        <Link
          href="mailto:conf@epicweb.dev?subject=Sponsoring Epic Web Conf 2024"
          className="inline-flex items-center gap-1 text-center text-[#93A1D7] transition hover:brightness-125"
        >
          <StarIcon className="w-4" stroke="currentColor" aria-hidden="true" />
          Become a sponsor
        </Link>
        <Link
          href="/conf/code-of-conduct"
          className="inline-flex items-center gap-1 text-center text-[#93A1D7] transition hover:brightness-125"
        >
          <DocumentIcon
            className="w-4"
            stroke="currentColor"
            aria-hidden="true"
          />
          Code of Conduct
        </Link>
      </div> */}
    </section>
  )
}

const sponsorsData = {
  platinum: [
    {
      name: 'Fly.io',
      url: 'https://fly.io',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 300 89"
          className="w-56"
        >
          <title>Fly.io</title>
          <path
            d="M66.522 11.742h10.954c9.743 0 17.653 7.91 17.653 17.653v38.517c0 9.743-7.91 17.653-17.653 17.653h-.863c-5.015-.784-7.19-2.288-8.87-3.559L53.816 70.555a1.968 1.968 0 00-2.466 0l-4.524 3.721-12.743-10.478a1.96 1.96 0 00-2.464 0L13.915 79.371c-3.562 2.898-5.919 2.363-7.447 2.195C2.52 78.326 0 73.411 0 67.912V29.395c0-9.743 7.91-17.653 17.654-17.653h10.93l-.02.044-.368 1.074-.115.368-.496 2.201-.068.385-.218 2.204-.029.605-.005.212.021 1.01.05.593.123.926.156.835.186.768.241.832.625 1.761.207.528 1.089 2.301.384.707 1.26 2.162.482.78 1.758 2.588.253.343 2.288 2.977.739.916 2.611 3.053.537.588 2.153 2.316.852.883 1.409 1.4-.282.236-.177.159c-.478.446-.932.92-1.357 1.418-.205.241-.4.49-.584.746a9.78 9.78 0 00-.755 1.233 7.336 7.336 0 00-.302.685 6.35 6.35 0 00-.409 1.86l-.008.551c.02.776.178 1.542.467 2.263.292.73.719 1.399 1.258 1.973.392.417.838.781 1.327 1.08a6.76 6.76 0 001.321.624 7.401 7.401 0 003.119.355h.005a7.04 7.04 0 002.573-.764c.345-.183.674-.395.982-.636a6.288 6.288 0 001.981-2.635c.325-.81.484-1.675.47-2.546l-.026-.456a6.43 6.43 0 00-.426-1.753 7.348 7.348 0 00-.33-.716 9.82 9.82 0 00-.777-1.229 12.31 12.31 0 00-.41-.525 16.823 16.823 0 00-1.515-1.587l-.382-.325.646-.637 2.774-2.897.959-1.053 1.491-1.678.97-1.134 1.383-1.685.937-1.177 1.375-1.839.694-.951.985-1.473.82-1.254 1.546-2.692.884-1.766.025-.054.677-1.638a.56.56 0 00.023-.065l.729-2.274.076-.331.317-1.81.061-.49.019-.3.022-1.133-.006-.211-.058-1.015-.072-.788-.359-2.272c-.006-.03-.012-.06-.021-.09l-.461-1.68-.159-.467zm5.207 48.032a4.262 4.262 0 00-4.183 4.183c.033 2.281 1.901 4.148 4.183 4.181 2.281-.033 4.149-1.9 4.184-4.181a4.263 4.263 0 00-4.184-4.183z"
            fill-opacity=".35"
            fill="currentColor"
          />
          <path
            d="M63.494 85.565h-21.51c-1.102 0-1.256-1.044-.547-1.638 10.499-8.795 10.474-8.824 10.474-8.824a1.158 1.158 0 011.484-.003s9.867 8.174 10.646 8.827c.709.594.556 1.638-.547 1.638zm-15.869-35.08l.112.032c.035.018.066.043.098.065l.093.082c.229.211.452.433.666.66.151.161.298.327.439.498.18.217.348.444.501.68.066.104.127.21.185.32.05.095.095.193.134.293.07.174.122.357.138.543l-.003.35a1.997 1.997 0 01-.947 1.556c-.35.212-.744.342-1.151.379l-.433.013-.365-.032a2.795 2.795 0 01-.501-.122 2.419 2.419 0 01-.475-.222l-.285-.209a1.952 1.952 0 01-.649-1.112 2.741 2.741 0 01-.034-.221l-.01-.333a1.63 1.63 0 01.039-.269c.05-.195.122-.384.212-.563.117-.225.25-.442.399-.648.247-.333.514-.651.8-.951.185-.195.373-.384.57-.565l.141-.127c.097-.065.098-.065.209-.097zm-.717-46.522l.045-.004V42.97l-.097-.18a109.393 109.393 0 01-3.584-7.262 77.906 77.906 0 01-2.432-6.089 48 48 0 01-1.465-5.013c-.309-1.348-.545-2.715-.642-4.096a19.74 19.74 0 01-.034-1.771c.009-.514.03-1.027.063-1.541.051-.807.133-1.613.251-2.412.094-.629.21-1.256.353-1.876.114-.492.246-.981.397-1.462.218-.695.478-1.374.782-2.037.111-.239.23-.476.356-.709.753-1.395 1.775-2.68 3.113-3.54a6.509 6.509 0 012.894-1.019zm5.059.098l2.624.749a16.01 16.01 0 013.461 1.763 13.88 13.88 0 013.722 3.712 14.085 14.085 0 011.441 2.779 16.272 16.272 0 011.003 4.499c.035.403.055.807.063 1.212.009.375.007.752-.023 1.127a12.76 12.76 0 01-.472 2.48 19.491 19.491 0 01-.68 1.951 27.983 27.983 0 01-1.08 2.332c-.875 1.69-1.881 3.306-2.959 4.869a75.975 75.975 0 01-4.267 5.568 105.805 105.805 0 01-5.119 5.674 108.121 108.121 0 003.369-6.894 85.38 85.38 0 001.638-3.947 61.629 61.629 0 001.526-4.439 43.79 43.79 0 00.878-3.404c.209-.984.374-1.978.476-2.978.085-.834.117-1.673.096-2.509a35.765 35.765 0 00-.057-1.536c-.123-2.057-.436-4.108-1.036-6.081a17.657 17.657 0 00-.827-2.193c-.768-1.673-1.84-3.237-3.291-4.378zM207.646 74.498l-12.457-26.666c-1.04-2.222-1.66-3.034-2.701-3.976l-1.012-.913c-.808-.783-1.338-1.508-1.338-2.369 0-1.233.976-2.271 2.755-2.271h10.815c1.697 0 2.755.883 2.755 2.201 0 .736-.338 1.288-.766 1.829-.507.643-1.168 1.269-1.168 2.412 0 .716.209 1.43.611 2.342l7.434 17.408 6.668-16.97c.41-1.129.69-2.123.69-2.919 0-1.239-.673-1.744-1.192-2.298-.452-.481-.813-.989-.813-1.804 0-1.331 1.083-2.201 2.541-2.201h6.66c1.867 0 2.755.966 2.755 2.201 0 .79-.455 1.523-1.351 2.313l-.942.774c-1.312 1.072-1.919 2.622-2.608 4.226l-10.099 24.329c-1.193 2.844-2.97 6.793-5.683 10.033-2.757 3.29-6.474 5.849-11.508 5.849-4.215 0-6.767-2.013-6.767-4.913 0-2.657 1.965-4.774 4.547-4.774 1.414 0 2.149.668 2.895 1.355.617.566 1.243 1.148 2.477 1.148 1.144 0 2.21-.484 3.177-1.266 1.476-1.194 2.712-3.073 3.625-5.08zm73.198-.095c5.607 0 10.201-1.921 13.784-5.755C298.209 64.82 300 60.212 300 54.824c0-5.259-1.702-9.58-5.093-12.966-3.391-3.385-7.79-5.085-13.204-5.085-5.701 0-10.363 1.851-13.994 5.539-3.632 3.691-5.449 8.162-5.449 13.415 0 5.205 1.734 9.618 5.195 13.237 3.464 3.622 7.925 5.439 13.389 5.439zm-47.413-.417c3.242 0 5.547-2.183 5.547-5.33 0-3.073-2.381-5.26-5.547-5.26-3.318 0-5.692 2.191-5.692 5.26 0 3.144 2.376 5.33 5.692 5.33zm11.544-4.988l.79-.834c.938-.912 1.24-1.833 1.24-4.373V48.776c0-2.196-.298-3.176-1.23-4.015l-.928-.832c-.91-.803-1.209-1.302-1.209-2.104 0-1.14.884-2.075 2.306-2.399l6.303-1.53c.603-.146 1.283-.288 1.81-.288.725 0 1.319.238 1.735.652.417.415.662 1.011.662 1.758v23.773c0 2.397.291 3.512 1.292 4.354a.478.478 0 01.045.045l.705.821c.885.861 1.252 1.432 1.252 2.217 0 1.401-1.056 2.202-2.754 2.202h-10.672c-1.618 0-2.684-.796-2.684-2.202 0-.789.368-1.365 1.337-2.23zm-71.334.001l.789-.835c.939-.912 1.241-1.833 1.241-4.373v-31.35c0-2.126-.225-3.17-1.22-4.008l-.953-.922c-.821-.798-1.121-1.293-1.121-2.091 0-1.142.888-2.073 2.232-2.399l6.231-1.529c.604-.146 1.283-.289 1.811-.289.72 0 1.329.217 1.764.628.436.413.704 1.026.704 1.852v40.108c0 2.403.299 3.454 1.304 4.363l.804.851c.891.867 1.188 1.434 1.188 2.223 0 .582-.178 1.051-.494 1.409-.446.504-1.193.793-2.19.793H175.06c-.996 0-1.744-.289-2.191-.793-.315-.358-.492-.827-.492-1.409 0-.79.293-1.362 1.264-2.229zm-29.058-16.145v10.798c0 1.627.322 3.129 1.662 4.366l.867.841c.971.942 1.266 1.506 1.266 2.369 0 1.319-1.057 2.202-2.755 2.202h-12.032c-1.698 0-2.755-.883-2.755-2.202 0-1.024.301-1.509 1.271-2.374l.864-.839c1.005-.91 1.661-2.151 1.661-4.363V34.805c0-1.758-.389-3.196-1.662-4.367l-.868-.841c-.892-.866-1.266-1.435-1.266-2.3 0-1.398 1.061-2.271 2.755-2.271h32.23c1.231 0 2.314.274 3.005.952.481.472.788 1.137.823 2.06l.502 7.44c.05.93-.225 1.706-.761 2.184-.37.329-.867.526-1.493.526-.789 0-1.394-.298-1.934-.812-.485-.462-.917-1.111-1.405-1.875-1.177-1.881-1.725-2.558-2.98-3.438-1.746-1.288-4.407-1.731-9.437-1.731-2.903 0-4.735.127-5.889.424-.746.193-1.177.443-1.407.81-.237.376-.262.851-.262 1.431v14.62h7.558c1.808 0 3.08-.327 4.554-2.341l.008-.012c.591-.752 1.019-1.306 1.426-1.673.473-.429.929-.632 1.532-.632a2.212 2.212 0 012.182 2.203v10.079c0 1.35-1.052 2.271-2.182 2.271-.562 0-1.018-.2-1.493-.62-.409-.359-.838-.898-1.394-1.617-1.613-2.088-2.752-2.421-4.633-2.421zm127.7-.255c0-3.616.788-6.215 2.42-7.778 1.613-1.543 3.346-2.324 5.209-2.324 2.563 0 4.873 1.435 6.956 4.255 2.128 2.884 3.181 6.781 3.181 11.686 0 3.62-.79 6.243-2.425 7.851-1.611 1.586-3.343 2.39-5.205 2.39-2.563 0-4.871-1.446-6.955-4.288-2.129-2.908-3.181-6.841-3.181-11.792zm-21.09-19.555c3.249 0 5.547-1.829 5.547-4.983 0-3.079-2.296-4.912-5.547-4.912-3.327 0-5.621 1.834-5.621 4.912s2.295 4.983 5.621 4.983z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Neon',
      url: 'https://neon.tech',
      logo: (
        <svg
          className="w-56"
          viewBox="0 0 158 45"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 7.61152C0 3.40779 3.44137 0 7.68651 0H36.8952C41.1404 0 44.5817 3.40779 44.5817 7.61152V32.2111C44.5817 36.5601 39.0241 38.4476 36.3287 35.014L27.902 24.2798V37.2964C27.902 41.0798 24.8048 44.1468 20.9842 44.1468H7.68651C3.44137 44.1468 0 40.739 0 36.5353V7.61152ZM7.68651 6.08921C6.83748 6.08921 6.14921 6.77077 6.14921 7.61152V36.5353C6.14921 37.376 6.83748 38.0576 7.68651 38.0576H21.2148C21.6393 38.0576 21.7528 37.7168 21.7528 37.2964V19.8412C21.7528 15.4922 27.3104 13.6047 30.0059 17.0383L38.4325 27.7725V7.61152C38.4325 6.77077 38.5129 6.08921 37.6639 6.08921H7.68651Z"
            fill="currentColor"
          />
          <path
            d="M36.8954 0C41.1406 0 44.5819 3.40779 44.5819 7.61152V32.2111C44.5819 36.5601 39.0243 38.4476 36.3289 35.014L27.9022 24.2798V37.2964C27.9022 41.0798 24.805 44.1468 20.9844 44.1468C21.4089 44.1468 21.753 43.806 21.753 43.3857V19.8412C21.753 15.4922 27.3106 13.6047 30.0061 17.0383L38.4327 27.7725V1.5223C38.4327 0.681558 37.7445 0 36.8954 0Z"
            fill="currentColor"
          />
          <path
            d="M75.1561 13.0033V24.5502L63.8496 13.0033H57.9648V31.8884H63.332V19.4782L75.6465 31.8884H80.5232V13.0033H75.1561Z"
            fill="currentColor"
          />
          <path
            d="M90.4725 27.6797V24.3343H102.487V20.3145H90.4725V17.212H105.048V13.0033H84.9964V31.8884H105.348V27.6797H90.4725Z"
            fill="currentColor"
          />
          <path
            d="M119.61 32.5089C127.157 32.5089 132.061 28.8398 132.061 22.4458C132.061 16.0519 127.157 12.3828 119.61 12.3828C112.063 12.3828 107.187 16.0519 107.187 22.4458C107.187 28.8398 112.063 32.5089 119.61 32.5089ZM119.61 28.0304C115.415 28.0304 112.826 26.007 112.826 22.4458C112.826 18.8847 115.442 16.8613 119.61 16.8613C123.806 16.8613 126.394 18.8847 126.394 22.4458C126.394 26.007 123.806 28.0304 119.61 28.0304Z"
            fill="currentColor"
          />
          <path
            d="M152.632 13.0033V24.5502L141.326 13.0033H135.441V31.8884H140.808V19.4782L153.123 31.8884H157.999V13.0033H152.632Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Mux',
      url: 'https://mux.link/epic-web',
      logo: (
        <svg
          className="w-32"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 189 60"
        >
          <path
            fill="#E7E7E9"
            d="M117.451 11.043a3.662 3.662 0 1 1 0-7.323 3.662 3.662 0 0 1 0 7.323Zm0-11.043a7.38 7.38 0 0 0-7.381 7.381v22.15c0 8.144-6.626 14.77-14.77 14.77-8.144 0-14.769-6.626-14.769-14.77V7.381a7.381 7.381 0 1 0-14.762 0v22.15c0 16.284 13.248 29.532 29.531 29.532 16.284 0 29.532-13.248 29.532-29.532V7.381A7.381 7.381 0 0 0 117.451 0Zm64.168 55.343a3.662 3.662 0 1 1 0-7.324 3.662 3.662 0 0 1 0 7.324ZM149.03 29.53l-16.931 16.931A7.382 7.382 0 0 0 142.538 56.9l16.931-16.931L176.4 56.9a7.38 7.38 0 1 0 10.438-10.438l-16.931-16.93v-.001l16.931-16.93a7.38 7.38 0 0 0 0-10.44 7.38 7.38 0 0 0-10.438 0l-16.931 16.931-16.931-16.93A7.382 7.382 0 0 0 132.099 12.6l16.931 16.931ZM51.681 55.344a3.662 3.662 0 1 1 0-7.324 3.662 3.662 0 0 1 0 7.324ZM54.506.562a7.381 7.381 0 0 0-8.044 1.6l-16.93 16.93L12.6 2.163A7.38 7.38 0 0 0 0 7.382v44.3a7.381 7.381 0 0 0 14.762 0V25.2l9.55 9.55a7.381 7.381 0 0 0 10.439 0l9.55-9.55v26.48a7.381 7.381 0 0 0 14.762 0V7.38A7.38 7.38 0 0 0 54.506.562Z"
          />
        </svg>
      ),
    },
  ],
  gold: [
    {
      name: 'Sentry.io',
      url: 'https://sentry.io/welcome',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 222 66"
          className="w-56"
        >
          <title>Sentry.io</title>
          <path
            d="M29,2.26a4.67,4.67,0,0,0-8,0L14.42,13.53A32.21,32.21,0,0,1,32.17,40.19H27.55A27.68,27.68,0,0,0,12.09,17.47L6,28a15.92,15.92,0,0,1,9.23,12.17H4.62A.76.76,0,0,1,4,39.06l2.94-5a10.74,10.74,0,0,0-3.36-1.9l-2.91,5a4.54,4.54,0,0,0,1.69,6.24A4.66,4.66,0,0,0,4.62,44H19.15a19.4,19.4,0,0,0-8-17.31l2.31-4A23.87,23.87,0,0,1,23.76,44H36.07a35.88,35.88,0,0,0-16.41-31.8l4.67-8a.77.77,0,0,1,1.05-.27c.53.29,20.29,34.77,20.66,35.17a.76.76,0,0,1-.68,1.13H40.6q.09,1.91,0,3.81h4.78A4.59,4.59,0,0,0,50,39.43a4.49,4.49,0,0,0-.62-2.28Z M124.32,28.28,109.56,9.22h-3.68V34.77h3.73V15.19l15.18,19.58h3.26V9.22h-3.73ZM87.15,23.54h13.23V20.22H87.14V12.53h14.93V9.21H83.34V34.77h18.92V31.45H87.14ZM71.59,20.3h0C66.44,19.06,65,18.08,65,15.7c0-2.14,1.89-3.59,4.71-3.59a12.06,12.06,0,0,1,7.07,2.55l2-2.83a14.1,14.1,0,0,0-9-3c-5.06,0-8.59,3-8.59,7.27,0,4.6,3,6.19,8.46,7.52C74.51,24.74,76,25.78,76,28.11s-2,3.77-5.09,3.77a12.34,12.34,0,0,1-8.3-3.26l-2.25,2.69a15.94,15.94,0,0,0,10.42,3.85c5.48,0,9-2.95,9-7.51C79.75,23.79,77.47,21.72,71.59,20.3ZM195.7,9.22l-7.69,12-7.64-12h-4.46L186,24.67V34.78h3.84V24.55L200,9.22Zm-64.63,3.46h8.37v22.1h3.84V12.68h8.37V9.22H131.08ZM169.41,24.8c3.86-1.07,6-3.77,6-7.63,0-4.91-3.59-8-9.38-8H154.67V34.76h3.8V25.58h6.45l6.48,9.2h4.44l-7-9.82Zm-10.95-2.5V12.6h7.17c3.74,0,5.88,1.77,5.88,4.84s-2.29,4.86-5.84,4.86Z"
            transform="translate(11, 11)"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Vercel',
      url: 'https://vercel.com',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 4438 1000"
          className="w-56"
        >
          <title>Vercel</title>
          <path
            d="M2223.75 250C2051.25 250 1926.87 362.5 1926.87 531.25C1926.87 700 2066.72 812.5 2239.38 812.5C2343.59 812.5 2435.47 771.25 2492.34 701.719L2372.81 632.656C2341.25 667.188 2293.28 687.344 2239.38 687.344C2164.53 687.344 2100.94 648.281 2077.34 585.781H2515.16C2518.59 568.281 2520.63 550.156 2520.63 531.094C2520.63 362.5 2396.41 250 2223.75 250ZM2076.09 476.562C2095.62 414.219 2149.06 375 2223.75 375C2298.59 375 2352.03 414.219 2371.41 476.562H2076.09ZM2040.78 78.125L1607.81 828.125L1174.69 78.125H1337.03L1607.66 546.875L1878.28 78.125H2040.78ZM577.344 0L1154.69 1000H0L577.344 0ZM3148.75 531.25C3148.75 625 3210 687.5 3305 687.5C3369.38 687.5 3417.66 658.281 3442.5 610.625L3562.5 679.844C3512.81 762.656 3419.69 812.5 3305 812.5C3132.34 812.5 3008.13 700 3008.13 531.25C3008.13 362.5 3132.5 250 3305 250C3419.69 250 3512.66 299.844 3562.5 382.656L3442.5 451.875C3417.66 404.219 3369.38 375 3305 375C3210.16 375 3148.75 437.5 3148.75 531.25ZM4437.5 78.125V796.875H4296.88V78.125H4437.5ZM3906.25 250C3733.75 250 3609.38 362.5 3609.38 531.25C3609.38 700 3749.38 812.5 3921.88 812.5C4026.09 812.5 4117.97 771.25 4174.84 701.719L4055.31 632.656C4023.75 667.188 3975.78 687.344 3921.88 687.344C3847.03 687.344 3783.44 648.281 3759.84 585.781H4197.66C4201.09 568.281 4203.12 550.156 4203.12 531.094C4203.12 362.5 4078.91 250 3906.25 250ZM3758.59 476.562C3778.13 414.219 3831.41 375 3906.25 375C3981.09 375 4034.53 414.219 4053.91 476.562H3758.59ZM2961.25 265.625V417.031C2945.63 412.5 2929.06 409.375 2911.25 409.375C2820.47 409.375 2755 471.875 2755 565.625V796.875H2614.38V265.625H2755V409.375C2755 330 2847.34 265.625 2961.25 265.625Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Lingo.dev',
      url: 'https://lingo.dev/?utm_source=epicwebconf2025&utm_marketing=conf',
      logo: (
        <svg
          className="w-56"
          viewBox="0 0 409 71"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.144041 25.3614L25.9356 53.4988C26.0384 53.6094 26.1817 53.6745 26.3333 53.6745H68.6175C69.0993 53.6745 69.3397 53.0927 68.9987 52.7517L50.4416 34.1948H35.8324V41.4998L18.7876 24.4551H0.54175C0.0709926 24.4551 -0.175207 25.0151 0.144041 25.3614Z"
            fill="currentColor"
          />
          <path
            d="M69.0973 33.2909L43.303 5.15391C43.2 5.04297 43.0567 4.97803 42.9052 4.97803H0.620994C0.136708 4.97803 -0.104082 5.55974 0.236811 5.90064L18.7938 24.4576H33.4035V17.1528L50.4481 34.1975H68.6968C69.1676 34.1975 69.4137 33.6373 69.0944 33.2909H69.0973Z"
            fill="currentColor"
          />
          <path
            d="M284.958 53.9158H276.497V45.5288H284.958V53.9158Z"
            fill="currentColor"
          />
          <path
            d="M298.84 52.2039C296.213 50.5698 294.137 48.2655 292.606 45.2997C291.077 42.3338 290.301 38.962 290.301 35.2029C290.301 31.4436 291.066 28.0909 292.606 25.1439C294.137 22.1971 296.224 19.9113 298.84 18.2774C301.467 16.6434 304.385 15.8311 307.606 15.8311C312.308 15.8311 316.069 17.6162 318.892 21.177H319.259V1.9375H326.608V53.9136H320.3L319.554 49.1624H319.186C316.314 52.8273 312.45 54.6598 307.606 54.6598C304.385 54.6598 301.467 53.8475 298.84 52.2134V52.2039ZM313.878 46.2629C315.529 45.224 316.842 43.7412 317.815 41.805C318.78 39.8781 319.259 37.668 319.259 35.1934C319.259 32.7188 318.78 30.5276 317.815 28.6197C316.853 26.7118 315.54 25.2384 313.878 24.1994C312.216 23.1605 310.42 22.641 308.494 22.641C306.567 22.641 304.706 23.1605 303.072 24.1994C301.437 25.2384 300.134 26.7118 299.17 28.6197C298.208 30.5276 297.726 32.7188 297.726 35.1934C297.726 37.668 298.208 39.9347 299.17 41.8429C300.134 43.7507 301.437 45.224 303.072 46.2629C304.706 47.3021 306.51 47.8216 308.494 47.8216C310.477 47.8216 312.216 47.3021 313.878 46.2629Z"
            fill="currentColor"
          />
          <path
            d="M343.915 45.4028C346.182 47.0839 348.495 48.2644 351.414 48.2644C353.493 48.2644 355.324 47.7921 356.912 46.8572C358.498 45.9128 359.678 44.7039 360.473 43.221H368.122C366.98 46.5362 364.977 49.2751 362.106 51.4287C359.234 53.5823 355.675 54.6591 351.414 54.6591C347.948 54.6591 344.803 53.8274 341.989 52.1749C339.163 50.5219 336.954 48.2172 335.338 45.2706C333.733 42.3238 332.929 38.9707 332.929 35.2117C332.929 31.4524 333.733 28.1751 335.338 25.2282C336.945 22.2814 339.163 19.9862 341.989 18.3239C344.811 16.671 347.948 15.8398 351.414 15.8398C354.88 15.8398 357.732 16.6332 360.435 18.22C363.135 19.8068 365.26 21.9791 366.819 24.7559C368.378 27.5329 369.163 30.6497 369.163 34.1066C369.163 35.8348 369.04 37.1479 368.792 38.0451H340.251L346.37 32.4063H361.898C361.653 29.4407 360.558 26.985 358.631 25.0582C356.702 23.1314 354.294 22.1586 351.433 22.1586C348.572 22.1586 345.964 23.1219 343.934 25.0582C341.904 26.985 340.695 29.4407 340.297 32.4063C340.297 32.4063 338.73 41.587 343.934 45.4311L343.915 45.4028Z"
            fill="currentColor"
          />
          <path
            d="M393.879 53.9153H383.111L369.972 16.5698H377.7L387.795 45.604H389.28L399.375 16.5698H407.102L393.887 53.9153H393.879Z"
            fill="currentColor"
          />
          <path
            d="M145.532 9.51267H137.514V2.08887H145.532V9.51267ZM145.164 53.9136H137.816V16.5681H145.164V53.9136Z"
            fill="currentColor"
          />
          <path
            d="M153.254 53.9148V16.5692H159.564L160.31 20.4322H160.678C161.613 19.2421 163.049 18.1843 164.985 17.2398C166.912 16.2953 169.141 15.8325 171.672 15.8325C174.544 15.8325 177.103 16.4937 179.36 17.797C181.609 19.1099 183.375 20.9517 184.631 23.3319C185.896 25.712 186.52 28.4323 186.52 31.5017V53.9243H179.172V32.0968C179.172 29.4238 178.303 27.1949 176.574 25.4098C174.846 23.6246 172.692 22.7368 170.114 22.7368C168.386 22.7368 166.789 23.1618 165.325 24.0025C163.861 24.843 162.718 25.9859 161.868 27.4217C161.028 28.857 160.603 30.4156 160.603 32.0968V53.9243H153.254V53.9148Z"
            fill="currentColor"
          />
          <path
            d="M201.623 67.4964C199.073 66.1646 197.127 64.4932 195.795 62.4812C194.463 60.4698 193.746 58.4389 193.642 56.3519H201.065C201.264 58.08 202.208 59.6102 203.89 60.9132C205.571 62.2267 207.904 62.8779 210.869 62.8779C213.835 62.8779 216.385 62.0089 218.218 60.2808C220.05 58.5428 220.967 56.2475 220.967 53.3765V47.4356H220.598C219.313 48.8713 217.698 50.0138 215.771 50.8545C213.845 51.6951 211.71 52.1202 209.387 52.1202C206.166 52.1202 203.266 51.3551 200.66 49.8155C198.062 48.2852 196.022 46.132 194.539 43.3552C193.056 40.5877 192.31 37.4426 192.31 33.9291C192.31 30.4155 193.056 27.2799 194.539 24.5407C196.022 21.7922 198.062 19.6576 200.66 18.1181C203.257 16.588 206.166 15.8135 209.387 15.8135C211.767 15.8135 213.948 16.2858 215.96 17.2208C217.963 18.1653 219.587 19.4215 220.825 21.0083H221.193L221.939 16.5502H228.324V53.4521C228.324 56.6159 227.634 59.4118 226.246 61.8389C224.858 64.2665 222.846 66.1462 220.191 67.478C217.547 68.8188 214.44 69.48 210.869 69.48C207.299 69.48 204.173 68.8094 201.623 67.478V67.4964ZM215.724 43.9975C217.33 43.034 218.605 41.6741 219.55 39.9172C220.494 38.1606 220.957 36.1674 220.957 33.9386C220.957 31.7095 220.485 29.7354 219.55 27.9976C218.605 26.2691 217.33 24.9185 215.724 23.9551C214.118 22.9917 212.324 22.51 210.341 22.51C207.271 22.51 204.74 23.5773 202.728 25.7024C200.726 27.8275 199.724 30.576 199.724 33.9479C199.724 37.3197 200.726 40.1343 202.728 42.2596C204.73 44.3846 207.271 45.4519 210.341 45.4519C212.324 45.4519 214.118 44.9702 215.724 44.007V43.9975Z"
            fill="currentColor"
          />
          <path
            d="M243.235 52.1674C240.411 50.5144 238.201 48.2101 236.586 45.2632C234.98 42.3164 234.177 38.9634 234.177 35.2043C234.177 31.445 234.98 28.1677 236.586 25.2209C238.191 22.274 240.411 19.9789 243.235 18.3165C246.06 16.6636 249.195 15.8325 252.661 15.8325C256.128 15.8325 259.254 16.6636 262.05 18.3165C264.845 19.9789 267.046 22.274 268.661 25.2209C270.267 28.1677 271.069 31.4924 271.069 35.2043C271.069 38.9162 270.267 42.3164 268.661 45.2632C267.055 48.2101 264.845 50.5144 262.05 52.1674C259.254 53.8298 256.118 54.6516 252.661 54.6516C249.204 54.6516 246.05 53.8204 243.235 52.1674ZM258.262 46.2643C259.972 45.2253 261.322 43.7425 262.305 41.8063C263.296 39.8794 263.788 37.6694 263.788 35.1948C263.788 32.7202 263.296 30.5289 262.305 28.6211C261.313 26.7131 259.962 25.2398 258.262 24.2008C256.552 23.1618 254.682 22.6424 252.661 22.6424C250.64 22.6424 248.761 23.1618 247.06 24.2008C245.351 25.2398 244 26.7131 243.018 28.6211C242.026 30.5289 241.535 32.7202 241.535 35.1948C241.535 37.6694 242.026 39.8701 243.018 41.8063C244.009 43.733 245.36 45.2253 247.06 46.2643C248.77 47.3033 250.64 47.8228 252.661 47.8228C254.682 47.8228 256.562 47.3033 258.262 46.2643Z"
            fill="currentColor"
          />
          <path
            d="M133.081 53.3381H100.118V1.3623H107.91V45.9143L108.052 53.3381L116.222 45.9143H133.081V53.3381Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
  ],
  silver: [
    {
      name: 'Prisma',
      url: 'http://prisma.io/?utm_source=event&utm_campaign=epicweb25',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-40"
          viewBox="0 0 90 28"
          fill="none"
        >
          <title>Prisma</title>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.209637 19.0073C-0.0659575 18.5726 -0.070121 18.0189 0.198904 17.58L10.3282 1.05707C10.8916 0.138071 12.252 0.218426 12.7033 1.19735L21.9569 21.2706C22.3002 22.0154 21.905 22.8917 21.1194 23.1274L6.72474 27.4458C6.14558 27.6195 5.52155 27.3867 5.19781 26.876L0.209637 19.0073ZM11.4969 5.88824C11.5945 5.40217 12.2605 5.33208 12.4572 5.78717L18.8402 20.5571C18.9603 20.8352 18.8108 21.1559 18.5205 21.2425L8.57492 24.2114C8.20935 24.3205 7.85916 24.0011 7.93428 23.627L11.4969 5.88824ZM48.4948 21.1371H51.3226V10.772H48.4948V21.1371ZM48.3744 8.09277C48.3744 7.17221 48.8878 6.7116 49.9137 6.7116C50.9394 6.7116 51.4527 7.17221 51.4527 8.09277C51.4527 8.53176 51.3244 8.87321 51.068 9.11743C50.8114 9.36165 50.4267 9.48343 49.9137 9.48343C48.8878 9.48343 48.3744 9.01987 48.3744 8.09277ZM87.1709 16.335L86.0768 16.372C85.2548 16.3969 84.6429 16.5455 84.2412 16.8172C83.8392 17.0893 83.6386 17.5031 83.6386 18.0596C83.6386 18.8567 84.0959 19.2555 85.0106 19.2555C85.6656 19.2555 86.1897 19.0672 86.5819 18.6898C86.9744 18.313 87.1709 17.8124 87.1709 17.1878V16.335ZM88.0053 21.1375L87.4583 19.7282H87.384C86.908 20.3279 86.4182 20.7437 85.9144 20.9755C85.4109 21.2072 84.7542 21.3228 83.9446 21.3228C82.9491 21.3228 82.1661 21.0386 81.5941 20.47C81.0226 19.9017 80.7368 19.0918 80.7368 18.0409C80.7368 16.941 81.1214 16.1298 81.8907 15.6073C82.6607 15.0851 83.8209 14.7961 85.3723 14.7404L87.1709 14.6848V14.2304C87.1709 13.1799 86.633 12.6544 85.5576 12.6544C84.7293 12.6544 83.7558 12.9045 82.6371 13.4054L81.7009 11.4955C82.8938 10.8716 84.2167 10.559 85.6689 10.559C87.0596 10.559 88.1258 10.8621 88.8676 11.4677C89.6091 12.0734 89.98 12.9946 89.98 14.2304V21.1375H88.0053ZM72.4802 21.1375H69.6524V15.0834C69.6524 14.3357 69.527 13.775 69.2772 13.4008C69.0265 13.0269 68.6326 12.8397 68.0948 12.8397C67.3719 12.8397 66.8465 13.1058 66.5188 13.6371C66.1911 14.1688 66.0275 15.0435 66.0275 16.261V21.1375H63.1996V10.7723H65.3599L65.74 12.0982H65.8978C66.1757 11.6225 66.5778 11.25 67.1029 10.9812C67.6283 10.7121 68.231 10.5776 68.9109 10.5776C70.4623 10.5776 71.5128 11.0846 72.0631 12.0982H72.3132C72.5915 11.616 73.001 11.2421 73.5419 10.9763C74.0827 10.7105 74.6929 10.5776 75.3728 10.5776C76.5471 10.5776 77.4359 10.8791 78.0382 11.4817C78.6409 12.0844 78.9421 13.0502 78.9421 14.3786V21.1375H76.1051V15.0834C76.1051 14.3357 75.9798 13.775 75.73 13.4008C75.4792 13.0269 75.0854 12.8397 74.5475 12.8397C73.8555 12.8397 73.3379 13.0872 72.9945 13.5815C72.6517 14.0761 72.4802 14.8608 72.4802 15.9362V21.1375ZM60.17 20.4885C60.9088 19.9323 61.2781 19.1227 61.2781 18.0594C61.2781 17.5468 61.1887 17.1045 61.0093 16.7336C60.8299 16.3627 60.5517 16.0353 60.1749 15.7508C59.7981 15.4667 59.2046 15.1609 58.3946 14.8332C57.4862 14.4685 56.8976 14.1932 56.6285 14.0079C56.3601 13.8226 56.2252 13.6033 56.2252 13.3496C56.2252 12.8985 56.6426 12.6729 57.477 12.6729C57.9465 12.6729 58.4071 12.7443 58.8582 12.886C59.3093 13.0284 59.7948 13.2104 60.314 13.4331L61.1668 11.3936C59.9863 10.8498 58.7718 10.5778 57.5232 10.5778C56.2127 10.5778 55.2009 10.8295 54.4872 11.3333C53.7729 11.8371 53.416 12.5495 53.416 13.4701C53.416 14.0079 53.5012 14.461 53.6714 14.8286C53.841 15.1963 54.113 15.5223 54.4872 15.8065C54.8607 16.091 55.4467 16.4 56.2438 16.7336C56.8 16.9686 57.2453 17.1742 57.5788 17.3503C57.9128 17.5265 58.1475 17.6843 58.2837 17.8231C58.4195 17.9622 58.4876 18.1429 58.4876 18.3655C58.4876 18.9587 57.9743 19.2553 56.9483 19.2553C56.4478 19.2553 55.8684 19.1718 55.2103 19.0052C54.5517 18.8382 53.9601 18.6313 53.4347 18.3838V20.7203C53.8983 20.918 54.3959 21.0679 54.9275 21.1701C55.4591 21.2719 56.1014 21.3229 56.8557 21.3229C58.3266 21.3229 59.4314 21.0447 60.17 20.4885ZM46.9948 10.661C46.7414 10.6054 46.4232 10.5776 46.0398 10.5776C45.3969 10.5776 44.8021 10.7553 44.2554 11.1108C43.708 11.4664 43.2739 11.9345 42.9524 12.5152H42.8136L42.3962 10.7723H40.2546V21.1375H43.0824V15.8622C43.0824 15.0278 43.3341 14.3786 43.8376 13.9151C44.3418 13.4515 45.0446 13.2197 45.9472 13.2197C46.2749 13.2197 46.5528 13.2508 46.7817 13.3124L46.9948 10.661ZM31.9317 13.9614H32.8774C33.7613 13.9614 34.4223 13.7869 34.8613 13.4376C35.3003 13.0886 35.5196 12.5799 35.5196 11.9124C35.5196 11.239 35.3356 10.7414 34.968 10.4199C34.6 10.0984 34.0239 9.93766 33.2388 9.93766H31.9317V13.9614ZM38.4214 11.8106C38.4214 13.2694 37.9657 14.385 37.0537 15.1573C36.1423 15.9302 34.8459 16.3162 33.1649 16.3162H31.9317V21.1373H29.0577V7.58296H33.3872C35.0315 7.58296 36.2814 7.93684 37.1375 8.64461C37.9936 9.35238 38.4214 10.4079 38.4214 11.8106Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'JetBrains',
      url: 'https://www.jetbrains.com/',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-48"
          fill="none"
          viewBox="0 0 298 64"
        >
          <path
            fill="currentColor"
            d="M86.484 40.586c0 .846-.179 1.593-.537 2.25a3.718 3.718 0 0 1-1.514 1.524c-.657.358-1.394.538-2.24.538H78v6.104h5.079c1.912 0 3.625-.428 5.148-1.285a9.36 9.36 0 0 0 3.585-3.545c.866-1.503 1.305-3.196 1.305-5.088V21.018h-6.633v19.568Zm17.856-1.823h13.891v-5.606H104.34v-6.363h15.355v-5.776H97.877v29.974h22.246v-5.776H104.34v-6.453Zm17.865-11.8h8.882v24.02h6.633v-24.02h8.842v-5.945h-24.367v5.955l.01-.01Zm47.022 9.002a7.85 7.85 0 0 0-1.673-.647 7.47 7.47 0 0 0 1.275-.488c1.096-.568 1.962-1.364 2.579-2.39.618-1.026.936-2.2.936-3.535 0-1.524-.418-2.888-1.244-4.093-.827-1.195-1.992-2.131-3.486-2.808-1.494-.668-3.206-1.006-5.118-1.006h-13.315v29.974h13.574c2.011 0 3.804-.348 5.387-1.055 1.573-.707 2.798-1.683 3.675-2.948.866-1.255 1.304-2.689 1.304-4.302 0-1.484-.338-2.808-1.026-3.983a7.05 7.05 0 0 0-2.858-2.729l-.01.01Zm-13.603-9.918h5.886c.816 0 1.533.15 2.161.438a3.353 3.353 0 0 1 1.464 1.255c.348.537.527 1.175.527 1.902 0 .727-.179 1.414-.527 1.981-.349.568-.837.996-1.464 1.305-.628.309-1.345.458-2.161.458h-5.886v-7.35.01Zm10.138 18.134c-.378.567-.916 1.006-1.603 1.314-.697.309-1.484.458-2.39.458h-6.145v-7.688h6.145c.886 0 1.673.17 2.37.498.687.329 1.235.787 1.613 1.385.378.597.578 1.274.578 2.041 0 .767-.19 1.424-.568 1.992Zm29.596-5.308c1.663-.797 2.947-1.922 3.864-3.366.916-1.444 1.374-3.117 1.374-5.029s-.448-3.525-1.344-4.959c-.897-1.434-2.171-2.54-3.814-3.326-1.644-.787-3.546-1.175-5.717-1.175h-13.124v29.974h6.642V40.078h4.322l6.084 10.914h7.578l-6.851-11.72c.339-.12.677-.25.996-.399h-.01Zm-2.151-6.124a3.599 3.599 0 0 1-1.583 1.444c-.688.338-1.494.507-2.42.507h-5.975v-8.295h5.975c.926 0 1.732.17 2.42.498a3.637 3.637 0 0 1 1.583 1.434c.368.617.558 1.355.558 2.19 0 .837-.19 1.574-.558 2.202v.02Zm20.594-11.731-10.706 29.974h6.742l2.121-6.612h11.114l2.27 6.612h6.612L220.99 21.018h-7.189Zm-.339 18.343 3.445-10.576.409-1.922.408 1.922 3.685 10.576h-7.947Zm20.693 11.631h6.851V21.018h-6.851v29.974Zm31.02-9.7-12.896-20.274h-6.463v29.974h6.055V30.717l12.826 20.275h6.533V21.018h-6.055v20.275Zm31.528-3.355c-.647-1.245-1.564-2.29-2.729-3.137-1.165-.846-2.509-1.404-4.023-1.693l-5.098-1.045c-.797-.19-1.434-.518-1.902-.996-.469-.478-.708-1.076-.708-1.783 0-.647.17-1.205.518-1.683.339-.478.827-.846 1.444-1.115.618-.269 1.335-.398 2.151-.398.817 0 1.554.139 2.181.418.627.279 1.115.667 1.464 1.175a2.96 2.96 0 0 1 .528 1.723h6.642c-.04-1.743-.528-3.287-1.444-4.621-.916-1.344-2.201-2.39-3.834-3.147-1.633-.757-3.505-1.135-5.597-1.135-2.091 0-3.943.388-5.566 1.175-1.623.787-2.898 1.872-3.804 3.266-.906 1.395-1.364 2.978-1.364 4.76 0 1.444.288 2.749.876 3.904a7.908 7.908 0 0 0 2.479 2.898c1.076.767 2.311 1.304 3.725 1.603l5.397 1.115c.886.21 1.584.598 2.101 1.156.518.557.767 1.244.767 2.08a3.03 3.03 0 0 1-.567 1.803c-.379.528-.907.936-1.584 1.225-.677.289-1.474.428-2.39.428-.916 0-1.782-.159-2.529-.478-.747-.318-1.325-.776-1.733-1.374-.418-.587-.617-1.275-.617-2.041h-6.642c.029 1.872.527 3.515 1.513 4.949.976 1.424 2.32 2.54 4.033 3.336 1.713.797 3.675 1.195 5.886 1.195 2.21 0 4.202-.408 5.915-1.225 1.723-.816 3.057-1.942 4.023-3.376.966-1.434 1.444-3.057 1.444-4.87 0-1.483-.329-2.847-.976-4.102l.02.01ZM13 47h17v4H13v-4Z"
          />
          <path
            fill="currentColor"
            fillRule="evenodd"
            d="M20.34 3.66 8 16l-4.34 4.34A12.504 12.504 0 0 0 0 29.18V59c0 2.76 2.24 5 5 5h29.82c3.32 0 6.49-1.32 8.84-3.66L48 56l12.34-12.34c2.34-2.34 3.66-5.52 3.66-8.84V5c0-2.76-2.24-5-5-5H29.18c-3.32 0-6.49 1.32-8.84 3.66ZM48 56V16H8v40h40Z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ],
  community: [
    {
      name: 'Fathom Analytics',
      url: 'https://usefathom.com',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-24"
          viewBox="0 0 523.801 234.398"
        >
          <title>Fathom Analytics</title>
          <path
            d="M28.654 33.217V29.26a3.987 3.987 0 00-3.982-3.988h-7.633v-3.666c-.013-4.285.956-6.852 1.828-7.786l.041-.051a4.462 4.462 0 012.155-1.176 52.035 52.035 0 018.52-.661h1.466a3.987 3.987 0 003.987-3.989V4.371A3.98 3.98 0 0031.811.456 42.91 42.91 0 0030.19.175C29.437.059 25.485 0 24.71 0h3.178-3.232C18.233 0 12.764 1.5 9.102 5.429l-.006.006C5.517 9.378 4.23 14.952 4.212 21.728V96.4a3.987 3.987 0 003.989 3.987h4.85a3.985 3.985 0 003.988-3.987V37.2h7.633a3.985 3.985 0 003.987-3.987M350.483 43.425a25.751 25.751 0 011.469 9.065v43.9a3.987 3.987 0 003.989 3.989h4.725a3.987 3.987 0 003.987-3.989V51.34a35.569 35.569 0 00-2.561-14.153 21.241 21.241 0 00-8.239-9.774 24.218 24.218 0 00-13.067-3.419h-.079a25.5 25.5 0 00-13.832 3.939 26.272 26.272 0 00-7.009 6.616 20.87 20.87 0 00-6.769-7.122 23.642 23.642 0 00-12.893-3.433h-.088a25.254 25.254 0 00-13.7 3.939 24.666 24.666 0 00-3.449 2.7v-1.378a3.987 3.987 0 00-3.985-3.989h-4.723a3.985 3.985 0 00-3.993 3.989v67.14a3.987 3.987 0 003.989 3.989h4.723a3.985 3.985 0 003.988-3.989V55.679a22.417 22.417 0 011.149-6.879 22.265 22.265 0 013.357-6.5 16.707 16.707 0 015.058-4.549 12.376 12.376 0 016.363-1.568c3.243.008 5.378.723 6.868 1.827a11.546 11.546 0 013.882 5.416 25.735 25.735 0 011.469 9.064v43.9a3.987 3.987 0 003.989 3.989h4.723a3.985 3.985 0 003.987-3.989V55.679a22.294 22.294 0 011.147-6.878 22.247 22.247 0 013.358-6.5 16.683 16.683 0 015.05-4.546 12.39 12.39 0 016.364-1.568c3.245.008 5.38.723 6.865 1.827a11.516 11.516 0 013.885 5.415M211.314 96.838a32.875 32.875 0 0017.776 4.694h.077a33.139 33.139 0 0017.48-4.536 29.573 29.573 0 0011.587-13.514c2.634-5.85 3.859-12.759 3.861-20.654a50.228 50.228 0 00-3.781-20.3 30.468 30.468 0 00-11.387-13.749 32.249 32.249 0 00-17.753-4.784h-.081a33.1 33.1 0 00-17.55 4.606 29.514 29.514 0 00-11.58 13.52c-2.594 5.827-3.788 12.783-3.791 20.833a50.622 50.622 0 003.708 20.149 29.551 29.551 0 0011.434 13.735zm3.076-54.253a15.989 15.989 0 016.085-4.774 21.569 21.569 0 018.657-1.629 20.364 20.364 0 018.437 1.656 16.447 16.447 0 016.1 4.916c3.488 4.464 5.471 11.01 5.475 20.071v.013c0 9.34-1.977 15.97-5.342 20.249a15.754 15.754 0 01-6.028 4.758 21.475 21.475 0 01-8.636 1.627c-6.669-.077-11.023-2.143-14.611-6.588-3.527-4.474-5.52-10.964-5.525-19.93v-.019c0-9.418 2-16.08 5.393-20.35M141.098 96.396V55.681a20.21 20.21 0 011.241-6.774 21.428 21.428 0 013.666-6.461 17.776 17.776 0 015.7-4.595 16.041 16.041 0 017.492-1.664c3.818 0 6.519.775 8.354 1.972a11.47 11.47 0 014.427 5.405 23.032 23.032 0 011.608 8.93v43.902a3.985 3.985 0 003.989 3.987h4.85a3.982 3.982 0 003.987-3.987V51.341a32.1 32.1 0 00-2.964-14.338 22.439 22.439 0 00-8.981-9.664 27.569 27.569 0 00-13.981-3.348h-.061a28.352 28.352 0 00-14.7 3.944 27.758 27.758 0 00-4.627 3.456V4.5a3.987 3.987 0 00-3.987-3.989h-4.722A3.985 3.985 0 00128.4 4.5v91.9a3.985 3.985 0 003.989 3.987h4.722a3.985 3.985 0 003.987-3.987M116.041 100.893h.01q1.149 0 2.242-.072a9.274 9.274 0 002.8-.589 3.989 3.989 0 002.52-3.712v-3.57a3.987 3.987 0 00-3.987-3.989h-1.873a18.9 18.9 0 01-5.418-.667 4.47 4.47 0 01-2.163-1.163l-.046-.054c-.873-.937-1.843-3.5-1.828-7.787V37.204h8.266a3.987 3.987 0 003.986-3.984v-3.96a3.987 3.987 0 00-3.987-3.989h-8.266V13.689A3.987 3.987 0 00104.31 9.7h-4.85a3.985 3.985 0 00-3.989 3.989v65.476c.013 6.7 1.319 12.253 4.861 16.208 3.671 4 9.2 5.515 15.7 5.514"
            fill="currentColor"
          />
          <path
            d="M523.065 112.594a3.985 3.985 0 00-3.251-1.68h-4.849a3.987 3.987 0 00-3.767 2.676l-30.251 86.79a3.988 3.988 0 003.767 5.3h4.85a3.985 3.985 0 003.765-2.674l30.249-86.791a3.994 3.994 0 00-.514-3.622"
            fill="currentColor"
          />
          <path
            d="M420.54 180.661zM338.96 114.997h-4.723a3.987 3.987 0 00-3.985 3.989v7.567a3.987 3.987 0 003.989 3.987h4.723a3.985 3.985 0 003.987-3.987v-7.567a3.985 3.985 0 00-3.987-3.989M338.96 142.52h-4.723a3.985 3.985 0 00-3.985 3.989v55.181a3.987 3.987 0 003.989 3.987h4.723a3.985 3.985 0 003.988-3.987v-55.181a3.985 3.985 0 00-3.987-3.989M321.476 194.258h-1.873a18.924 18.924 0 01-5.42-.665 4.484 4.484 0 01-2.162-1.163l-.045-.056c-.874-.937-1.843-3.505-1.83-7.792v-42.08h8.266a3.985 3.985 0 003.987-3.987v-3.958a3.985 3.985 0 00-3.987-3.989h-8.266v-11.582a3.987 3.987 0 00-3.987-3.989h-4.85a3.987 3.987 0 00-3.989 3.989v65.477c.013 6.7 1.319 12.253 4.861 16.206l.006.006c3.673 4 9.2 5.519 15.713 5.515h.063c.743 0 1.468-.026 2.173-.072a9.274 9.274 0 002.8-.592 3.988 3.988 0 002.527-3.708v-3.574a3.985 3.985 0 00-3.987-3.989M289.25 130.568h-4.979a3.981 3.981 0 00-3.706 2.513l-20.289 50.982-20.813-51.014a3.981 3.981 0 00-3.693-2.481h-5.231a3.987 3.987 0 00-3.687 5.512l26.564 64.224-2.581 6.406c-1.675 4.183-3.127 7.432-4.3 9.651a17.929 17.929 0 01-2.937 4.346 3.986 3.986 0 01-2.048 1.134 25 25 0 01-4.727.369h-2.2a3.987 3.987 0 00-3.989 3.989v3.83a3.992 3.992 0 003.215 3.912c.539.105 1.142.2 1.838.3a18.24 18.24 0 002.384.163 31.961 31.961 0 007.813-.825 14.291 14.291 0 006.621-3.789 28.48 28.48 0 004.8-6.9c1.447-2.77 3.02-6.248 4.754-10.493l30.891-76.344a3.987 3.987 0 00-3.7-5.485M217.775 105.807h-4.722a3.987 3.987 0 00-3.989 3.989v91.9a3.985 3.985 0 003.989 3.987h4.722a3.987 3.987 0 003.987-3.987v-91.9a3.987 3.987 0 00-3.987-3.989M102.779 141.48c3.816 0 6.516.775 8.35 1.97a11.471 11.471 0 014.437 5.403 23.006 23.006 0 011.607 8.931v43.9a3.987 3.987 0 003.989 3.987h4.85a3.985 3.985 0 003.989-3.987v-45.046a32.144 32.144 0 00-2.964-14.338 22.447 22.447 0 00-8.982-9.665 27.565 27.565 0 00-13.971-3.342h-.078a28.693 28.693 0 00-14.665 3.86 26.927 26.927 0 00-4.657 3.435v-2.035a3.987 3.987 0 00-3.987-3.989h-4.722a3.985 3.985 0 00-3.989 3.989v67.14a3.985 3.985 0 003.989 3.987h4.722a3.985 3.985 0 003.987-3.987v-40.714a20.252 20.252 0 011.241-6.776 21.4 21.4 0 013.666-6.463 17.776 17.776 0 015.7-4.595 16.065 16.065 0 017.492-1.664M12.864 204.392a33.879 33.879 0 0013.588 2.5 30.98 30.98 0 0010.476-1.959 33.289 33.289 0 0010.3-6.075c.6-.511 1.163-1.056 1.712-1.616a18.809 18.809 0 002.326 6.6 3.959 3.959 0 003.4 1.9h.008l2.326-.025a3.988 3.988 0 003.942-3.987V159a37.382 37.382 0 00-2.9-15.276 23.016 23.016 0 00-9.461-10.687c-4.317-2.545-9.549-3.681-15.492-3.681a35.269 35.269 0 00-15.457 3.14A24.812 24.812 0 007.1 141.909l-.018.029a3.46 3.46 0 00-.515 1.816 3.638 3.638 0 002.446 3.423l3.914 1.393a5.583 5.583 0 006.2-1.753 15.946 15.946 0 011.173-1.301c2.561-2.486 6.361-3.95 12.274-3.973a21.349 21.349 0 017.486 1.166 10.23 10.23 0 014.416 3.1c2.216 2.719 3.61 6.921 3.74 12.944-5.7.089-10.753.327-15.12.735a71.036 71.036 0 00-16.135 3.358h-.018l-.046.014h-.008c-5.147 1.6-9.4 4.092-12.387 7.668A20.779 20.779 0 000 184.189v.129a22.056 22.056 0 003.39 12.215 21.118 21.118 0 009.474 7.859m32.473-20.784a23.505 23.505 0 01-7.875 8.205 19.342 19.342 0 01-10.63 3.019c-5.021-.01-8.387-1.161-10.661-3.039a8.868 8.868 0 01-2.494-3.229 11.531 11.531 0 01-.852-4.552 10.344 10.344 0 011.331-5.239c.675-1.208 2.167-2.62 4.895-3.926 2.594-1.256 6.792-2.415 12.379-3.223a138.8 138.8 0 0116.816-1.181v2.467a20.863 20.863 0 01-2.908 10.7M185.724 133.037c-4.317-2.545-9.549-3.681-15.492-3.681a35.269 35.269 0 00-15.457 3.14 24.812 24.812 0 00-10.53 9.413l-.018.029a3.431 3.431 0 00-.515 1.816 3.638 3.638 0 002.444 3.423l3.914 1.393a5.58 5.58 0 006.2-1.753 15.966 15.966 0 011.182-1.294c2.561-2.486 6.361-3.95 12.274-3.973a21.349 21.349 0 017.486 1.166 10.23 10.23 0 014.416 3.1c2.216 2.719 3.61 6.921 3.74 12.944-5.7.089-10.753.327-15.12.735a71.036 71.036 0 00-16.135 3.358l-.018.006-.046.013h-.008c-5.147 1.6-9.4 4.092-12.387 7.668a20.779 20.779 0 00-4.5 13.646v.129a22.03 22.03 0 003.39 12.215 21.114 21.114 0 009.472 7.859 33.892 33.892 0 0013.59 2.5 30.98 30.98 0 0010.476-1.959 33.289 33.289 0 0010.3-6.075c.6-.511 1.163-1.056 1.712-1.616a18.809 18.809 0 002.326 6.6 3.959 3.959 0 003.4 1.9h.008l2.334-.027a3.987 3.987 0 003.942-3.987v-42.73a37.383 37.383 0 00-2.9-15.276 23.016 23.016 0 00-9.467-10.686m-3.254 50.576a23.505 23.505 0 01-7.875 8.205 19.342 19.342 0 01-10.63 3.019c-5.021-.01-8.387-1.161-10.661-3.039a8.868 8.868 0 01-2.494-3.229 11.5 11.5 0 01-.852-4.552 10.344 10.344 0 011.331-5.239c.675-1.208 2.167-2.62 4.895-3.926 2.594-1.256 6.792-2.415 12.379-3.223a138.8 138.8 0 0116.816-1.181v2.467a20.863 20.863 0 01-2.908 10.7M30.769 91.237a21.121 21.121 0 009.472 7.857 33.892 33.892 0 0013.59 2.5 30.941 30.941 0 0010.476-1.959 33.285 33.285 0 0010.3-6.074c.6-.511 1.163-1.058 1.712-1.616a18.821 18.821 0 002.326 6.6 3.958 3.958 0 003.4 1.9h.008l2.334-.027a3.987 3.987 0 003.931-3.986V53.703a37.4 37.4 0 00-2.9-15.278 23.005 23.005 0 00-9.467-10.685c-4.317-2.545-9.549-3.682-15.492-3.682a35.27 35.27 0 00-15.447 3.141 24.8 24.8 0 00-10.53 9.413l-.018.027a3.449 3.449 0 00-.517 1.817 3.639 3.639 0 002.445 3.423l3.914 1.393a5.519 5.519 0 001.855.321 5.616 5.616 0 004.344-2.072 15.985 15.985 0 011.182-1.3c2.561-2.486 6.361-3.95 12.274-3.973a21.382 21.382 0 017.486 1.168 10.2 10.2 0 014.416 3.1c2.216 2.717 3.61 6.919 3.74 12.942-5.7.089-10.753.329-15.12.735a71.037 71.037 0 00-16.135 3.36h-.018l-.046.014h-.008c-5.147 1.6-9.4 4.091-12.387 7.668a20.779 20.779 0 00-4.5 13.646v.129a22.038 22.038 0 003.39 12.215m41.939-12.914a23.524 23.524 0 01-7.875 8.207 19.354 19.354 0 01-10.63 3.017c-5.021-.008-8.387-1.16-10.661-3.038a8.859 8.859 0 01-2.494-3.231 11.488 11.488 0 01-.852-4.55 10.346 10.346 0 011.329-5.237c.675-1.206 2.167-2.62 4.895-3.926 2.594-1.256 6.792-2.415 12.379-3.221a138.43 138.43 0 0116.816-1.182v2.468a20.857 20.857 0 01-2.908 10.7M397.781 190.866c-3.216 2.572-7.034 3.878-12.106 3.9-6.677-.073-11.042-2.132-14.622-6.535-3.512-4.415-5.512-10.924-5.515-19.981v-.022c0-9.429 1.966-16.114 5.29-20.38a15.612 15.612 0 015.949-4.75 20.986 20.986 0 018.515-1.621 18.214 18.214 0 0112.13 4.062 17.127 17.127 0 014.225 5.3 3.229 3.229 0 003.915 1.616l5.992-2.088a3.214 3.214 0 001.795-4.467 29.734 29.734 0 00-2.467-4.2 28.315 28.315 0 00-10.9-9.263 33.375 33.375 0 00-14.665-3.141h-.081a32.19 32.19 0 00-17.315 4.625 29.766 29.766 0 00-11.422 13.5c-2.6 5.827-3.789 12.784-3.791 20.833a50.163 50.163 0 003.711 20.1 29.983 29.983 0 0011.407 13.71 32.338 32.338 0 0017.666 4.77h.126a32.675 32.675 0 0019.989-6.281 28.651 28.651 0 009.456-13.242 3.183 3.183 0 00-1.958-4.1l-5.865-2.045a3.162 3.162 0 00-4 1.8 18.2 18.2 0 01-5.464 7.905M477.642 171.453a18.27 18.27 0 00-6.117-5.48 19.176 19.176 0 00-2.071-1.021 43.51 43.51 0 00-9.7-2.677q-4.334-.692-8.717-1.222c-.361-.045-.718-.1-1.08-.139a47.73 47.73 0 01-8.186-1.645 9.605 9.605 0 01-4.611-2.779c-.793-.9-1.412-2.364-1.426-5.075a10.228 10.228 0 01.774-4.064 8.947 8.947 0 012.331-3.086c1.98-1.734 5.26-2.907 10.394-2.91 4.841.024 8.311 1.24 11.1 3.547a14.579 14.579 0 014.265 6.8 3.18 3.18 0 004.059 1.974l6.01-2.095a3.189 3.189 0 001.993-4 27.29 27.29 0 00-3.192-6.567 25.077 25.077 0 00-10.308-8.918 32.554 32.554 0 00-13.789-2.794h-.034a30.876 30.876 0 00-14.18 2.99 21.1 21.1 0 00-12 19.33v.075c0 4.427.911 8.387 3.17 11.549a17.923 17.923 0 006.034 5.322 19.329 19.329 0 002.186 1.039 45.5 45.5 0 009.644 2.593c2.133.341 4.268.645 6.4.929 1.109.148 2.216.3 3.325.432a39.317 39.317 0 017.94 1.733 10.551 10.551 0 014.676 2.952c.766.865 1.41 2.392 1.422 5.2a11.592 11.592 0 01-.9 4.644 10.47 10.47 0 01-2.719 3.588c-2.315 2.033-6.109 3.354-11.924 3.357-5.632-.019-9.751-1.412-13-4.024a16.468 16.468 0 01-4.9-6.768 3.158 3.158 0 00-3.974-1.744l-5.844 2.037a3.182 3.182 0 00-1.935 4.162 27.346 27.346 0 008.417 11.54c5.643 4.466 12.834 6.6 21.034 6.6h.112a36.427 36.427 0 0014.136-2.6 24.011 24.011 0 0010.37-8.039 21.652 21.652 0 003.962-12.946v-.07c0-4.513-.9-8.526-3.148-11.738"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Convex',
      url: 'https://convex.dev',
      logo: (
        <svg
          className="w-48"
          viewBox="0 0 382 146"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M114.794 86.6648C111.454 83.6785 109.784 79.2644 109.784 73.434C109.784 67.6036 111.487 63.1896 114.896 60.2033C118.301 57.217 122.959 55.721 128.865 55.721C131.319 55.721 133.486 55.8973 135.372 56.2613C137.258 56.6197 139.063 57.2283 140.786 58.0929V67.5524C138.106 66.2157 135.064 65.5445 131.659 65.5445C128.66 65.5445 126.445 66.1417 125.018 67.3363C123.586 68.5308 122.873 70.5615 122.873 73.434C122.873 76.2099 123.575 78.2178 124.986 79.4578C126.391 80.7035 128.617 81.3236 131.665 81.3236C134.891 81.3236 137.955 80.5329 140.862 78.9573V88.8547C137.636 90.3849 133.615 91.1471 128.801 91.1471C122.797 91.1471 118.133 89.6511 114.794 86.6648Z"
            fill="currentColor"
          />
          <path
            d="M143.77 73.4279C143.77 67.643 145.337 63.246 148.471 60.2312C151.605 57.2165 156.328 55.7148 162.645 55.7148C169.006 55.7148 173.761 57.2222 176.922 60.2312C180.078 63.2403 181.656 67.643 181.656 73.4279C181.656 85.2366 175.318 91.1409 162.645 91.1409C150.06 91.1466 143.77 85.2423 143.77 73.4279ZM167.179 79.4574C168.109 78.2116 168.574 76.2037 168.574 73.4335C168.574 70.7089 168.109 68.7123 167.179 67.4439C166.25 66.1754 164.737 65.544 162.645 65.544C160.603 65.544 159.122 66.1811 158.214 67.4439C157.306 68.7123 156.853 70.7089 156.853 73.4335C156.853 76.2094 157.306 78.2173 158.214 79.4574C159.122 80.7031 160.597 81.3231 162.645 81.3231C164.737 81.3231 166.244 80.6974 167.179 79.4574Z"
            fill="currentColor"
          />
          <path
            d="M184.638 56.4315H196.629L196.97 59.014C198.288 58.0583 199.969 57.2677 202.011 56.6477C204.054 56.0276 206.167 55.7148 208.35 55.7148C212.392 55.7148 215.343 56.7671 217.207 58.8718C219.071 60.9764 220.001 64.2244 220.001 68.627V90.4299H207.194V69.9865C207.194 68.4564 206.864 67.3585 206.205 66.6873C205.546 66.0161 204.443 65.6862 202.898 65.6862C201.947 65.6862 200.968 65.9137 199.969 66.3688C198.969 66.8239 198.131 67.4097 197.445 68.1265V90.4299H184.638V56.4315Z"
            fill="currentColor"
          />
          <path
            d="M220.038 56.4317H233.391L239.524 76.3689L245.658 56.4317H259.011L246.268 90.4301H232.775L220.038 56.4317Z"
            fill="currentColor"
          />
          <path
            d="M263.043 87.5062C259.195 84.4687 257.396 79.1957 257.396 73.5018C257.396 67.9558 258.828 63.3882 262.097 60.2312C265.366 57.0743 270.349 55.7148 276.639 55.7148C282.426 55.7148 286.976 57.1255 290.3 59.9468C293.618 62.7682 295.282 66.6191 295.282 71.4939V77.4494H270.927C271.532 79.2184 272.299 80.4983 274.185 81.289C276.071 82.0796 278.703 82.4721 282.07 82.4721C284.08 82.4721 286.133 82.3071 288.219 81.9715C288.954 81.8521 290.165 81.6644 290.802 81.5222V89.7871C287.619 90.6972 283.377 91.1523 278.595 91.1523C272.159 91.1466 266.89 90.5437 263.043 87.5062ZM281.826 70.1344C281.826 68.4507 279.984 64.8273 276.282 64.8273C272.942 64.8273 270.738 68.3938 270.738 70.1344H281.826Z"
            fill="currentColor"
          />
          <path
            d="M305.338 73.1437L293.346 56.4317H307.245L331.773 90.4301H317.74L312.287 82.825L306.835 90.4301H292.865L305.338 73.1437Z"
            fill="currentColor"
          />
          <path
            d="M317.431 56.4317H331.265L320.647 71.3178L313.622 61.7786L317.431 56.4317Z"
            fill="currentColor"
          />
          <path
            d="M82.2808 87.6517C89.652 86.8381 96.6012 82.9353 100.427 76.4211C98.6156 92.533 80.8853 102.717 66.413 96.4643C65.0795 95.8897 63.9316 94.9339 63.1438 93.705C59.8915 88.6302 58.8224 82.1729 60.3585 76.313C64.7475 83.8399 73.6717 88.4538 82.2808 87.6517Z"
            fill="currentColor"
          />
          <path
            d="M60.0895 71.5852C57.1016 78.4464 56.9722 86.4797 60.6353 93.0906C47.7442 83.453 47.8848 62.8294 60.4778 53.2885C61.6425 52.4067 63.0267 51.8833 64.4785 51.8036C70.4486 51.4907 76.5144 53.7835 80.7683 58.0561C72.1254 58.1415 63.7076 63.643 60.0895 71.5852Z"
            fill="currentColor"
          />
          <path
            d="M84.9366 60.1673C80.5757 54.1253 73.7503 50.0119 66.2722 49.8868C80.7277 43.3669 98.5086 53.9375 100.444 69.5659C100.624 71.0167 100.388 72.4959 99.7409 73.8044C97.04 79.2547 92.032 83.4819 86.1801 85.0464C90.4678 77.144 89.9388 67.4893 84.9366 60.1673Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Remi',
      url: 'https://remihq.com/careers',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-20"
          viewBox="0 0 1254 465"
          fill="none"
        >
          <g clip-path="url(#clip0_76_636)">
            <path
              d="M89.9244 190.107V136.156H0V459.861H89.9244V302.503C89.9244 247.262 109.836 210.663 154.153 210.663H207.459V136.156H170.86C131.682 136.156 107.276 149.639 89.9439 190.107H89.9244Z"
              fill="currentColor"
            />
            <path
              d="M1253.45 136.156H1163.53V459.861H1253.45V136.156Z"
              fill="currentColor"
            />
            <path
              d="M1253.45 0H1163.53V89.9244H1253.45V0Z"
              fill="currentColor"
            />
            <path
              d="M1095.37 241.322C1091.38 179.731 1040.15 131.018 977.561 131.018C975.744 131.018 973.926 131.076 972.129 131.154C971.503 131.174 970.898 131.213 970.272 131.252C969.178 131.311 968.103 131.408 967.029 131.487C906.004 136.723 865.107 185.574 865.107 185.574C844.14 152.766 807.385 130.998 765.549 130.998C727.25 130.998 693.211 149.229 671.658 177.484V136.137H581.733V459.822H671.658V267.819C671.658 234.112 698.975 206.775 732.682 206.775C766.389 206.775 793.726 234.112 793.726 267.819V459.822H883.631V267.819C883.631 234.112 910.968 206.775 944.675 206.775C978.382 206.775 1005.7 234.112 1005.7 267.819V459.822H1095.62V249.08C1095.62 246.462 1095.55 243.863 1095.35 241.303L1095.37 241.322Z"
              fill="currentColor"
            />
            <path
              d="M310.339 261.41H453.432C450.833 225.436 425.47 196.536 381.876 196.536C338.281 196.536 312.938 225.436 310.339 261.41ZM310.339 317.92C310.339 323.958 313.407 391.783 388.304 391.783C430.746 391.783 447.746 369.624 453.432 350.045L541.793 350.025C523.035 412.144 470.53 465 385.764 465C277.609 465 221.958 378.925 221.958 297.364C221.958 215.803 272.059 131.018 381.876 131.018C491.692 131.018 541.793 215.158 541.793 297.364V317.92H310.339Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_76_636">
              <rect width="1253.45" height="465" fill="currentColor" />
            </clipPath>
          </defs>
        </svg>
      ),
    },
    {
      name: 'Arcjet',
      url: 'https://arcjet.com/?ref=epic-web-2025',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-40"
          fill="none"
          viewBox="0 0 1040 460"
        >
          <path
            fill="currentColor"
            d="M730.356 287.811c-23.741 0-40.79-17.686-40.79-42.064 0-24.219 17.049-41.745 40.152-41.745s40.312 17.526 40.312 41.745c0 1.274-.16 3.186-.319 4.78H707.89c1.752 13.065 10.994 22.147 23.103 22.147 8.604 0 16.571-4.621 20.554-11.472l14.34 9.241c-7.17 10.357-21.032 17.368-35.531 17.368m-21.67-50.827h42.224c-2.868-10.676-10.994-17.846-21.192-17.846-10.197 0-18.164 7.17-21.032 17.846m-54.387 50.558v-81.451h18.477v85.818c0 11.254-2.183 19.485-6.719 24.356-5.207 5.879-12.43 8.735-21.668 8.735l-5.04-16.629q8.316 0 11.591-4.536c2.183-3.191 3.359-8.566 3.359-16.293m20.811-111.867c0 6.411-5.197 11.608-11.609 11.608-6.411 0-11.608-5.197-11.608-11.608s5.197-11.609 11.608-11.609 11.609 5.197 11.609 11.609m117.102-.005h18.482v30.403h16.093v15.137h-16.093v64.529h-18.482V221.21h-13.013v-15.137h13.013z"
          />
          <path
            fill="currentColor"
            fillRule="evenodd"
            d="m398.096 285.739 14.944-30.251h56.808l14.761 30.251h19.276L441.442 158 379 285.739zm22.686-46.072 20.66-42.1 20.661 42.1z"
            clipRule="evenodd"
          />
          <path
            fill="currentColor"
            d="M636.593 233.276c-5.783-7.374-12.766-13.353-25.807-13.353-13.544 0-23.422 10.846-23.422 25.983 0 14.977 9.878 25.971 23.422 25.971 13.041 0 20.024-6.117 25.807-13.475v20.959c-6.947 5.322-15.795 8.45-25.807 8.45-24.06 0-41.427-17.686-41.427-41.905 0-24.378 17.367-41.904 41.427-41.904 9.978 0 18.872 3.034 25.807 8.324zm-103.542 52.463h-18.436v-79.627h18.436v7.629c3.552-3.979 6.508-6.305 8.909-7.437 2.842-1.339 5.983-2.203 9.67-2.203s9.914 1.508 15.11 4.693l-8.38 16.76c-3.52-2.514-6.872-3.687-10.224-3.687q-15.085 0-15.085 22.626zm-247.787-56.687c12.613 3.223 23.038 5.806 44.736 10.618-21.698 4.812-32.123 7.395-44.736 10.618a11.99 11.99 0 0 0-8.646 8.646c-3.223 12.609-5.807 23.039-10.618 44.736-4.811-21.697-7.395-32.127-10.618-44.736a11.99 11.99 0 0 0-8.646-8.646c-12.613-3.223-23.038-5.806-44.736-10.618 21.698-4.812 32.123-7.395 44.736-10.618a11.99 11.99 0 0 0 8.646-8.646c3.223-12.609 5.807-23.039 10.618-44.736 4.811 21.697 7.395 32.127 10.618 44.736a11.99 11.99 0 0 0 8.646 8.646"
          />
          <path
            fill="currentColor"
            fillRule="evenodd"
            d="m174.499 236.17 3.501 3.501-3.501 3.501c-32.237-2.368-62.608-2.703-111.542-3.242-7.226-.08-14.856-.164-22.957-.259 7.979-.094 15.502-.177 22.63-.256 49.126-.541 79.56-.877 111.869-3.245M956 230c0 79.642-72.998 145.735-151.51 145.75h-.054q-.593 0-1.185-.005C883.17 375.073 947.75 310.078 947.75 230S883.17 84.927 803.25 84.255q.586-.005 1.172-.005h.082C883.011 84.273 956 150.363 956 230"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      name: 'Handsontable',
      url: 'https://handsontable.com/docs/react-data-grid/?utm_source=EpicWeb2025&utm_medium=referral',
      logo: (
        <svg
          className="w-44"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1300 197.33459"
        >
          <path
            fill="currentColor"
            d="M50,108.21949H87.31046v49.44708h42.5603V108.21949h37.12505V237.71215H129.87076V186.644H87.31046v51.06813H50Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M182.72415,211.447c0-22.27079,18.838-30.96932,50.34767-30.96932h9.05876v-2.172c0-9.04817-2.172-15.03437-12.49686-15.03437-8.87335,0-12.13662,5.25514-12.85709,11.23075h-30.429c1.45152-22.82173,19.37834-34.04189,45.27793-34.04189,26.07971,0,42.74042,10.67981,42.74042,35.85364v61.3983H242.85634V226.66152c-4.52938,6.88678-12.86239,13.22262-28.07157,13.22262C197.75852,239.88414,182.72415,231.55643,182.72415,211.447Zm59.40643-6.87619v-5.79549h-8.51312c-13.0372,0-18.83269,2.53222-18.83269,10.31958,0,5.43526,3.6182,8.87865,11.22545,8.87865C235.2491,217.97358,242.13058,212.71844,242.13058,204.57084Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M292.81729,142.99243h32.42084v15.21449c5.06973-9.77923,15.38931-17.74671,31.14944-17.74671,18.29235,0,30.96932,11.41087,30.96932,35.67353v61.57841H354.93605V182.66029c0-10.69041-4.16386-15.94555-13.58285-15.94555-9.59912,0-16.11507,5.79549-16.11507,17.93742v53.06H292.81729Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M401.09337,191.34823V189.8967c0-32.23013,17.7467-49.43649,40.2082-49.43649,14.849,0,23.72231,6.33584,29.15757,15.935V101.33271h32.41555V237.71215H470.45914v-15.3946c-4.7095,9.77923-15.21449,17.56659-29.5231,17.56659C419.02548,239.88414,401.09337,224.1293,401.09337,191.34823Zm70.27165-.54035v-1.45152c0-15.93495-6.70137-24.45337-18.65788-24.45337-12.13133,0-18.65258,8.15818-18.65258,24.63348V190.988c0,15.935,7.0616,24.08255,18.29235,24.08255C464.118,215.07054,471.365,206.92294,471.365,190.80788Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M515.71059,207.64341h29.88333c1.086,6.88678,4.16915,10.6904,13.40273,10.6904,7.96748,0,11.591-3.08316,11.591-7.78736,0-4.52408-4.52408-6.70666-17.56659-8.50782-25.35394-3.26328-36.039-10.6904-36.039-30.429,0-21.01,18.65258-31.14944,40.56844-31.14944,23.36207,0,39.66255,7.96748,41.83454,30.6091H570.04728c-1.2714-6.34644-4.70949-9.419-12.13662-9.419-7.0616,0-10.68511,3.07256-10.68511,7.41653,0,4.53468,4.344,6.34643,15.93495,7.78736,26.26512,3.26328,38.9421,10.15006,38.9421,30.429,0,22.4615-16.66071,32.601-42.92583,32.601C532.19118,239.88414,516.43635,228.65339,515.71059,207.64341Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M611.14017,191.52834v-1.45152c0-30.41838,22.63632-49.61661,51.974-49.61661,29.16286,0,51.61906,18.64729,51.61906,48.716v1.45152c0,30.78921-22.64161,49.25638-51.79918,49.25638S611.14017,222.13743,611.14017,191.52834Zm70.63188-.54035v-1.45152c0-15.935-6.34113-24.8136-18.65787-24.8136-12.13133,0-18.8327,8.51842-18.8327,24.45337v1.45152c0,16.29518,6.34114,25.17383,18.8327,25.17383C675.2508,215.80159,681.77205,206.74283,681.77205,190.988Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M728.83525,142.99243H761.2561v15.21449c5.06973-9.77923,15.3893-17.74671,31.14943-17.74671,18.29235,0,30.96933,11.41087,30.96933,35.67353v61.57841H790.954V182.66029c0-10.69041-4.16385-15.94555-13.58285-15.94555-9.59911,0-16.11506,5.79549-16.11506,17.93742v53.06H728.83525Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M846.1648,207.28318V165.0831H834.75923V142.99243H846.1648V123.25386h32.42085v19.73857h18.65258V165.0831H878.58565v39.12751c0,6.87619,3.44339,9.95935,9.59911,9.95935a24.63418,24.63418,0,0,0,9.23888-1.63164V237.1718a68.33784,68.33784,0,0,1-17.93212,2.71234C858.12131,239.88414,846.1648,229.56456,846.1648,207.28318Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M906.82145,211.447c0-22.27079,18.838-30.96932,50.34766-30.96932h9.05876v-2.172c0-9.04817-2.172-15.03437-12.49685-15.03437-8.87336,0-12.13663,5.25514-12.85709,11.23075H910.445c1.45152-22.82173,19.37834-34.04189,45.27794-34.04189,26.0797,0,42.74041,10.67981,42.74041,35.85364v61.3983H966.95363V226.66152c-4.52938,6.88678-12.86238,13.22262-28.07157,13.22262C921.85582,239.88414,906.82145,231.55643,906.82145,211.447Zm59.40642-6.87619v-5.79549h-8.51312c-13.0372,0-18.83269,2.53222-18.83269,10.31958,0,5.43526,3.61821,8.87865,11.22545,8.87865C959.34639,217.97358,966.22787,212.71844,966.22787,204.57084Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M1049.33542,223.038v14.67414h-32.42084V101.33271h32.42084v55.78292a33.42707,33.42707,0,0,1,29.5178-16.65542c22.096,0,40.02809,16.65542,40.02809,49.07626V190.988c0,32.601-17.93212,48.89615-40.2082,48.89615C1064.54461,239.88414,1054.03962,233.18807,1049.33542,223.038Zm36.5794-32.05v-1.45152c0-16.666-7.24171-24.63348-18.65258-24.63348-11.7711,0-18.83269,8.51842-18.83269,24.45337v1.45152c0,16.11506,7.06159,24.26266,18.83269,24.26266C1079.39887,215.07054,1085.91482,206.56271,1085.91482,190.988Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M1134.24943,101.33271h32.23543V237.71215h-32.23543Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M1181.50863,191.52834v-1.45152c0-30.41838,22.63632-49.61661,51.25354-49.61661,25.89959,0,48.17568,14.84367,48.17568,48.89615v8.50782h-66.64814c.90587,12.50215,8.14759,19.74917,19.73857,19.74917,10.68511,0,15.21449-4.89491,16.4806-11.23075h30.429c-2.89774,21.37021-19.37834,33.50154-47.99556,33.50154C1203.23907,239.88414,1181.50863,222.49766,1181.50863,191.52834Zm67.91425-11.95121c-.54564-11.41087-6.15572-17.74671-16.66071-17.74671-9.77923,0-16.48059,6.33584-18.11223,17.74671Z"
            transform="translate(-50 -101.33271)"
          />
          <path
            fill="currentColor"
            d="M1295.33225,243.99954H1350v54.66775h-54.66775Z"
            transform="translate(-50 -101.33271)"
          />
        </svg>
      ),
    },
    {
      name: 'Gel',
      url: 'https://www.geldata.com/',
      logo: (
        <svg
          className="w-[70px]"
          viewBox="0 0 157 122"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 54.3455C0 71.6156 13.9429 85.5584 31.213 85.5584C48.3247 85.5584 62.426 71.6156 62.426 54.3455C62.426 37.2338 48.3247 23.1325 31.213 23.1325C13.9429 23.1325 0 37.2338 0 54.3455ZM133.725 11.0909C133.725 5.22857 138.636 0 144.816 0C150.995 0 156.223 5.22857 156.223 11.0909V74.4675C156.223 80.3299 150.995 85.5584 144.816 85.5584C138.636 85.5584 133.725 80.3299 133.725 74.4675V11.0909ZM8.23896 101.086C9.50649 112.018 18.5377 122 31.213 122C43.7299 122 53.0779 112.018 54.3455 101.086C56.0883 85.2416 41.6701 90.4701 31.213 90.4701C20.7558 90.4701 6.4961 85.2416 8.23896 101.086ZM108.849 58.7818C109.958 56.2468 112.81 54.6623 118.356 54.6623C127.07 54.5039 130.556 48.8 125.961 39.7688C122.792 33.5896 117.881 28.8364 111.226 25.826C95.5403 18.8545 77.0026 25.9844 70.0312 41.6701C63.0597 57.1974 70.0312 75.7351 85.8753 82.8649C92.5299 85.7169 99.1844 86.1922 105.997 84.4493C115.821 81.7558 117.881 75.2597 112.018 68.7636C108.374 64.4857 107.582 61.4753 108.849 58.7818Z"
            fill="currentColor"
          />
        </svg>
      ),
    },
    {
      name: 'Tanstack',
      url: 'https://tanstack.com/',
      logo: (
        <Image
          className="saturate-0 transition hover:saturate-100"
          src="https://res.cloudinary.com/epic-web/image/upload/v1739288320/tanstack-logo.png"
          alt="Tanstack"
          width={210 / 1.1}
          height={42 / 1.1}
        />
      ),
    },
    {
      name: 'Bolt',
      url: 'https://bolt.new',
      logo: (
        <svg
          className="w-20"
          viewBox="0 0 1583 683"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g>
            <path
              d="M748.69 601.861C603.784 601.861 530.06 517.12 530.06 411.195C530.06 285.779 630.053 172.227 774.959 172.227C919.865 172.227 993.59 256.968 993.59 362.893C993.59 488.309 893.596 601.861 748.69 601.861ZM754.622 467.124C805.466 467.124 837.667 421.364 837.667 370.52C837.667 329.844 811.398 306.964 769.028 306.964C718.183 306.964 685.982 352.724 685.982 403.568C685.982 444.244 712.252 467.124 754.622 467.124Z"
              fill="white"
            />
            <path
              d="M1147.94 591.692H995.41L1119.98 26.474H1272.51L1147.94 591.692Z"
              fill="white"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M298.97 601.861C252.363 601.861 206.603 584.913 180.334 548.474L171.068 591.449L0 682.329L18.4676 591.449L143.048 26.474H295.581L251.516 225.614C287.107 186.633 320.155 172.227 362.526 172.227C454.045 172.227 515.058 232.393 515.058 342.555C515.058 456.107 444.724 601.861 298.97 601.861ZM357.441 374.757C357.441 427.296 320.155 467.124 271.853 467.124C244.737 467.124 220.162 456.955 204.061 439.159L227.789 334.929C245.584 317.133 265.922 306.964 289.649 306.964C326.087 306.964 357.441 334.081 357.441 374.757Z"
              fill="white"
            />
            <path
              d="M1431.21 601.861C1343.08 601.861 1280.37 569.659 1280.37 498.478C1280.37 492.546 1281.22 478.14 1283.76 466.276L1316.81 314.591H1249.02L1278.67 182.396H1346.47L1371.04 70.539L1541.8 0L1523.58 70.7114L1499 182.396H1582.04L1552.39 314.591H1469.34L1447.31 414.585C1445.61 422.211 1444.77 430.685 1444.77 434.075C1444.77 453.565 1456.63 467.124 1482.9 467.124C1490.53 467.124 1501.54 464.581 1504.08 462.887V584.913C1487.98 595.929 1459.17 601.861 1431.21 601.861Z"
              fill="white"
            />
          </g>
        </svg>
      ),
    },
    {
      name: 'Clerk',
      url: 'https://clerk.com',
      logo: (
        <svg
          className="h-10"
          viewBox="0 0 110 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse
            cx="16.0003"
            cy="16"
            rx="4.99998"
            ry="5"
            fill="currentColor"
          />
          <path
            d="M25.0091 27.8382C25.4345 28.2636 25.3918 28.9679 24.8919 29.3027C22.3488 31.0062 19.2899 31.9997 15.9991 31.9997C12.7082 31.9997 9.64935 31.0062 7.10616 29.3027C6.60633 28.9679 6.56361 28.2636 6.98901 27.8382L10.6429 24.1843C10.9732 23.854 11.4855 23.8019 11.9012 24.0148C13.1303 24.6445 14.5232 24.9997 15.9991 24.9997C17.4749 24.9997 18.8678 24.6445 20.0969 24.0148C20.5126 23.8019 21.0249 23.854 21.3552 24.1843L25.0091 27.8382Z"
            fill="currentColor"
          />
          <path
            opacity="0.6"
            d="M24.8928 2.697C25.3926 3.0318 25.4353 3.73609 25.0099 4.16149L21.356 7.81544C21.0258 8.14569 20.5134 8.19785 20.0978 7.98491C18.8687 7.35525 17.4758 7 15.9999 7C11.0294 7 6.99997 11.0294 6.99997 16C6.99997 17.4759 7.35522 18.8688 7.98488 20.0979C8.19782 20.5136 8.14565 21.0259 7.81541 21.3561L4.16147 25.0101C3.73607 25.4355 3.03178 25.3927 2.69698 24.8929C0.993522 22.3497 0 19.2909 0 16C0 7.16344 7.16341 0 15.9999 0C19.2908 0 22.3496 0.993529 24.8928 2.697Z"
            fill="currentColor"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M100.405 21.2489C100.421 21.2324 100.442 21.2231 100.465 21.2231C100.493 21.2231 100.518 21.2375 100.533 21.2613L105.275 28.8821C105.321 28.9554 105.401 29 105.487 29L109.75 29C109.946 29 110.066 28.7848 109.963 28.6183L103.457 18.1226C103.399 18.0278 103.41 17.9056 103.485 17.823L109.752 10.908C109.898 10.7473 109.784 10.4901 109.567 10.4901H105.12C105.05 10.4901 104.983 10.5194 104.936 10.5711L97.6842 18.4755C97.5301 18.6435 97.25 18.5345 97.25 18.3065V3.25C97.25 3.11193 97.138 3 97 3H93.25C93.1119 3 93 3.11193 93 3.25V28.75C93 28.8881 93.1119 29 93.25 29L97 29C97.138 29 97.25 28.8881 97.25 28.75V24.7373C97.25 24.6741 97.2739 24.6132 97.317 24.567L100.405 21.2489ZM52.2502 3.25C52.2502 3.11193 52.3621 3 52.5002 3H56.2501C56.3882 3 56.5001 3.11193 56.5001 3.25V28.75C56.5001 28.8881 56.3882 29 56.2501 29H52.5002C52.3621 29 52.2502 28.8881 52.2502 28.75V3.25ZM46.958 23.5912C46.8584 23.5052 46.7094 23.5117 46.6137 23.602C46.0293 24.1537 45.3447 24.595 44.5947 24.9028C43.7719 25.2407 42.8873 25.4108 41.995 25.4028C41.2415 25.4252 40.4913 25.2963 39.7906 25.0241C39.09 24.7519 38.4537 24.3422 37.9209 23.8202C36.9531 22.8322 36.396 21.4215 36.396 19.7399C36.396 16.3735 38.6356 14.0709 41.995 14.0709C42.896 14.0585 43.7888 14.241 44.6094 14.6052C45.3533 14.9355 46.0214 15.4077 46.5748 15.9934C46.6694 16.0936 46.8266 16.1052 46.9309 16.015L49.4625 13.8244C49.5659 13.7349 49.5785 13.5786 49.4873 13.4767C47.583 11.3488 44.5997 10.25 41.7627 10.25C36.0506 10.25 32.0003 14.1031 32.0003 19.7719C32.0003 22.5756 33.0069 24.9365 34.7044 26.6036C36.402 28.2707 38.8203 29.25 41.6108 29.25C45.1097 29.25 47.9259 27.9082 49.577 26.187C49.6739 26.086 49.6632 25.9252 49.5572 25.8338L46.958 23.5912ZM77.1575 20.9877C77.1436 21.1129 77.0371 21.2066 76.9111 21.2066H63.7746C63.615 21.2066 63.4961 21.3546 63.5377 21.5087C64.1913 23.9314 66.1398 25.3973 68.7994 25.3973C69.6959 25.4161 70.5846 25.2317 71.3968 24.8582C72.1536 24.5102 72.8249 24.0068 73.3659 23.3828C73.4314 23.3073 73.5454 23.2961 73.622 23.3602L76.2631 25.6596C76.3641 25.7476 76.3782 25.8999 76.2915 26.0021C74.697 27.8832 72.1135 29.25 68.5683 29.25C63.1142 29.25 59.0001 25.4731 59.0001 19.7348C59.0001 16.9197 59.9693 14.559 61.5847 12.8921C62.4374 12.0349 63.4597 11.3584 64.5882 10.9043C65.7168 10.4502 66.9281 10.2281 68.1473 10.2517C73.6753 10.2517 77.25 14.1394 77.25 19.5075C77.2431 20.0021 77.2123 20.4961 77.1575 20.9877ZM63.6166 17.5038C63.5702 17.6581 63.6894 17.8084 63.8505 17.8084H72.5852C72.7467 17.8084 72.8659 17.6572 72.8211 17.5021C72.2257 15.4416 70.7153 14.0666 68.3696 14.0666C67.6796 14.0447 66.993 14.1696 66.3565 14.4326C65.7203 14.6957 65.149 15.0908 64.6823 15.5907C64.1914 16.1473 63.8285 16.7998 63.6166 17.5038ZM90.2473 10.2527C90.3864 10.2512 90.5 10.3636 90.5 10.5027V14.7013C90.5 14.8469 90.3762 14.9615 90.2311 14.9508C89.8258 14.9207 89.4427 14.8952 89.1916 14.8952C85.9204 14.8952 84 17.1975 84 20.2195V28.75C84 28.8881 83.8881 29 83.75 29H80C79.862 29 79.75 28.8881 79.75 28.75V10.7623C79.75 10.6242 79.862 10.5123 80 10.5123H83.75C83.8881 10.5123 84 10.6242 84 10.7623V13.287C84 13.3013 84.0116 13.3128 84.0258 13.3128C84.034 13.3128 84.0416 13.3089 84.0465 13.3024C85.5124 11.3448 87.676 10.2559 89.9617 10.2559L90.2473 10.2527Z"
            fill="white"
          />
        </svg>
      ),
    },
    {
      name: 'Charlie',
      url: 'https://www.charlielabs.ai/',
      logo: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8"
          fill="none"
          viewBox="0 0 2787 697"
        >
          <path
            fill="currentColor"
            d="M2786.26 472.37h-57.98v60.92h57.98v-60.92ZM290.94.28 630.51 0c87.74 10.81 122.95 110.7 60.02 174.78-44.47 45.28-93.31 86.66-137.99 131.79-17.62 19.39-20.64 47.97-8 70.94 38.43 44.27 84 82.67 126 123.79 22.4 21.93 43.43 39.41 49.99 71.9 12.15 60.11-30.59 117.76-91.96 121.92-110.63-3.93-224.73 5.09-334.96 0C136.79 687.87 8.6 559.58 1.21 403c-1.61-34.1-1.61-76.84 0-110.94C8.55 136.62 135.43 8.69 290.94.28Zm61.96 97.86c-61.77 7.35-45.3 104.73 17.99 87.89 51.97-13.83 37.36-94.47-17.99-87.89Zm195.93 1.99c-61.77 7.35-45.3 104.73 17.99 87.89 51.97-13.83 37.36-94.47-17.99-87.89Zm1869.56 277.36c3.26 51.89 32.93 100.65 86.15 112.17 48.01 10.39 110.36-7.25 120.77-61.24h52.98c-8.84 58.2-55.61 96.26-111.78 106.54-132.15 24.19-222.3-85.86-194.43-212.22 18.55-84.11 88.53-129.76 173.63-119.9 83.98 9.74 134.91 79.28 129.57 162.15-.23 3.6-.78 9.87-2.49 12.49h-254.41l.01.01Zm203.92-41.94c-2.83-31.48-16.56-60.71-44.68-76.7-26.04-14.81-62.98-16.84-91.23-7.64-37.01 12.04-59.95 46.77-65.02 84.35h200.93v-.01ZM1818.6 533.29h-51.98v-55.93l-15.48 22.98c-59.9 63.59-203.5 49.04-209.48-53.4-3.35-57.45 36-93.63 89.18-104.74 38.97-8.14 78.84.19 117.17-3.78 20.08-2.08 20.25-15.38 17.48-32.19-4.29-25.96-22.6-46.19-47.31-54.61-49.44-16.83-119.41 1.41-127.49 59.96h-47.48c-1.07 0-1.49-2.19-1.44-3.34.8-18.41 14.08-44.16 25.96-58.05 71.59-83.69 247.4-58.67 250.93 66.84l-.05 216.26h-.01Zm-51.98-155.8c-31.74 1.32-64.88-1.76-96.46.02-35.12 1.97-72.79 17.47-75.35 57.54-4.19 65.51 82.8 69.97 125.49 45.49 36.52-20.95 48.07-63.14 46.32-103.04v-.01ZM1250.8 72.89v194.74l7.19-14.29c63.91-96.5 220.76-51.35 221.72 66.73v213.21h-51.97l.04-202.27c-12.71-121.87-169.58-101.57-177.02 14.98l.04 187.3h-51.98V72.89h51.98Zm-111.96 243.68h-50.98c-14.16-70.25-114.68-88.26-164.41-44.91-52.58 45.83-50.92 163.38 8.56 203.11 50.74 33.9 139.09 17.08 155.85-47.35h50.98c-5.59 29.56-19.05 56.68-41.97 76.41-37.68 32.43-99.55 41.27-146.97 29.95-154.16-36.79-157.9-281.35-7.76-325.34 82.19-24.08 184.91 16.01 196.69 108.12l.01.01ZM2173.47 72.89v460.4h-50.48l-1.5-1.5V72.89h51.98Zm-232.91 133.83v63.92l10.48-22.99c26.24-46.24 72.72-41.38 119.48-40.93v45.94c-38.19.9-75.61-7.14-102.99 25.44-21.04 25.05-24.18 61.52-26.96 92.91v162.28h-51.99V206.72h51.98Zm366.87 0h-51.98v326.57h51.98V206.72Zm4-133.83v71.41l-1.5 1.5h-58.48V72.89h59.98Z"
          />
        </svg>
      ),
    },
    {
      name: 'Echobind',
      url: 'https://echobind.com/',
      logo: (
        <svg
          className="h-12"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 221 62"
        >
          <path
            fill="currentColor"
            fillRule="evenodd"
            d="M78.31 32.476h7.058a.324.324 0 0 0 .338-.338 3.597 3.597 0 0 0-3.834-3.672 3.672 3.672 0 0 0-3.9 3.672.325.325 0 0 0 .338.338Zm3.533 7.498c1.763 0 3.092-.881 3.496-2.137a.733.733 0 0 1 .778-.58h1.733a.543.543 0 0 1 .58.675c-.403 2.938-3.055 5.09-6.61 5.09a6.742 6.742 0 0 1-6.91-6.44 36.895 36.895 0 0 1 0-4.753 6.742 6.742 0 0 1 6.955-6.448c4.105 0 6.786 2.549 6.786 6.786v2.35a.683.683 0 0 1-.676.676h-9.73a.324.324 0 0 0-.339.338c-.007 3.084 1.594 4.443 3.937 4.443Zm20.461-2.82a.565.565 0 0 1 .646-.58h1.763a.683.683 0 0 1 .676.676c0 3.29-2.747 5.772-6.551 5.772-4.076 0-6.515-2.886-6.786-6.448a37.15 37.15 0 0 1 0-4.752c.271-3.561 2.717-6.448 6.786-6.448 3.804 0 6.551 2.475 6.551 5.773a.604.604 0 0 1-.581.676h-1.836a.58.58 0 0 1-.646-.544c0-1.594-1.395-2.85-3.496-2.85a3.536 3.536 0 0 0-3.73 3.393 36.895 36.895 0 0 0 0 4.752 3.532 3.532 0 0 0 3.73 3.393c2.086.007 3.474-1.212 3.474-2.813Zm7.667 5.538a.683.683 0 0 1-.676-.676V19.374a.683.683 0 0 1 .676-.676h1.697a.682.682 0 0 1 .675.676v6.147c0 .405.329.734.735.734.675 0 1.564-.852 3.121-.852a6.447 6.447 0 0 1 6.448 6.427v10.179a.683.683 0 0 1-.676.675h-1.696a.683.683 0 0 1-.676-.675v-10.18a3.525 3.525 0 0 0-3.628-3.392c-2.108 0-3.635 1.468-3.635 3.224v10.348a.683.683 0 0 1-.676.675l-1.689.008Zm26.512-6.111c.103-1.582.103-3.17 0-4.751a3.943 3.943 0 0 0-7.806 0 36.931 36.931 0 0 0 0 4.752 3.942 3.942 0 0 0 7.806 0Zm3.056-4.751a36.931 36.931 0 0 1 0 4.752 6.977 6.977 0 0 1-13.918 0 36.931 36.931 0 0 1 0-4.752 6.978 6.978 0 0 1 13.918 0Zm4.817 10.766a.733.733 0 0 1-.734.507h-.734a.639.639 0 0 1-.544-.947l9.746-23.061a.639.639 0 0 1 .646-.507h.735a.656.656 0 0 1 .543.948l-9.658 23.06Zm23.766-6.015a36.93 36.93 0 0 0 0-4.751 3.944 3.944 0 0 0-7.807 0 36.93 36.93 0 0 0 0 4.751 3.945 3.945 0 0 0 7.807 0Zm-7.344 5.332a1.28 1.28 0 0 0-.852.544.644.644 0 0 1-.477.235h-1.153a.682.682 0 0 1-.683-.676V19.374a.682.682 0 0 1 .683-.676h1.696a.683.683 0 0 1 .676.676v6.007c0 .647.235.852.734.852.676 0 1.594-.852 3.158-.852 3.562 0 6.177 2.887 6.449 6.449a36.93 36.93 0 0 1 0 4.751c-.272 3.562-2.887 6.448-6.449 6.448-2.489 0-2.798-1.116-3.782-1.116Zm22.415-20.336a.538.538 0 0 1-.544.543h-2.306a.534.534 0 0 1-.543-.543v-2.343a.557.557 0 0 1 .543-.543h2.306a.559.559 0 0 1 .544.543v2.343Zm-7.668 7.028a.682.682 0 0 1-.675-.675v-1.535a.679.679 0 0 1 .675-.69h6.889c.375 0 .679.3.683.675v13.088a.324.324 0 0 0 .338.337h4.311a.683.683 0 0 1 .676.676v1.528a.684.684 0 0 1-.676.675h-12.559a.682.682 0 0 1-.675-.675V40.48a.682.682 0 0 1 .675-.676h4.444a.32.32 0 0 0 .243-.094.32.32 0 0 0 .094-.243V28.943a.32.32 0 0 0-.094-.243.32.32 0 0 0-.243-.095h-4.106Zm22.767-3.224a6.34 6.34 0 0 1 6.448 6.448v10.18a.683.683 0 0 1-.676.675h-1.696a.684.684 0 0 1-.676-.675v-10.18c0-1.968-1.63-3.393-3.731-3.393-2.1 0-3.73 1.256-3.73 3.225v10.347a.683.683 0 0 1-.683.676h-1.719a.683.683 0 0 1-.676-.675V26.394a.683.683 0 0 1 .676-.676h1.469a.655.655 0 0 1 .477.235c.198.29.505.486.852.544 1.006 0 1.278-1.117 3.665-1.117Zm19.976 11.2a36.93 36.93 0 0 0 0-4.751 3.943 3.943 0 0 0-7.807 0 37.295 37.295 0 0 0 0 4.751 3.945 3.945 0 0 0 7.807 0Zm-10.693 0a37.295 37.295 0 0 1 0-4.751c.272-3.562 2.886-6.449 6.448-6.449 1.564 0 2.475.852 3.158.852.507 0 .735-.205.735-.852v-6.007a.683.683 0 0 1 .675-.676h1.733a.683.683 0 0 1 .676.676v22.635a.683.683 0 0 1-.676.675h-1.175a.648.648 0 0 1-.477-.235 1.283 1.283 0 0 0-.852-.543c-.984 0-1.292 1.116-3.804 1.116-3.555.007-6.177-2.879-6.441-6.44Z"
            clipRule="evenodd"
          />
          <path
            fill="currentColor"
            d="M32.508 61.647h-3.326C12.92 60.783 0 47.323 0 30.845 0 13.81 13.81 0 30.845 0a30.843 30.843 0 0 1 30.846 30.845c0 16.478-12.92 29.938-29.183 30.802ZM45.35 32.439a7.03 7.03 0 0 1 .852 4.098 7.182 7.182 0 0 1-7.212 6.382h-9.048a.207.207 0 0 1-.19-.134.207.207 0 0 1 .058-.226 11.02 11.02 0 0 0 2.592-3.473.462.462 0 0 1 .412-.28h6.36a2.938 2.938 0 0 0 2.937-3.341c-.19-1.469-1.065-2.563-2.453-2.563h-4.333a.301.301 0 0 1-.279-.412l1.47-3.334c.087-.22.308-.367.94-.367h5.294a3.026 3.026 0 0 0 3.033-2.563 2.938 2.938 0 0 0-2.937-3.342H40.34c-4.134 0-5.78 3.716-7.469 7.961C30.56 36.45 28.525 42.92 20.924 42.92h-1.888a7.197 7.197 0 0 1-7.285-6.86 7.065 7.065 0 0 1 4.568-6.807 7.024 7.024 0 0 1-.852-4.098 7.182 7.182 0 0 1 7.212-6.382h9.07a.206.206 0 0 1 .132.36 11.02 11.02 0 0 0-2.593 3.473.463.463 0 0 1-.41.28h-6.36a2.937 2.937 0 0 0-2.939 3.34c.191 1.47 1.065 2.564 2.453 2.564h4.334a.3.3 0 0 1 .28.411l-1.47 3.335c-.088.22-.309.367-.94.367h-5.303a3.025 3.025 0 0 0-3.033 2.563 2.939 2.939 0 0 0 2.938 3.341h2.511c4.135 0 5.78-3.716 7.47-7.96 2.313-5.604 4.347-12.074 11.948-12.074h1.888a7.197 7.197 0 0 1 7.285 6.86 7.066 7.066 0 0 1-4.59 6.807ZM30.845 57.58c14.758-.017 26.717-11.976 26.733-26.734 0-14.764-11.969-26.732-26.733-26.732-14.764 0-26.732 11.968-26.732 26.732 0 14.765 11.968 26.733 26.732 26.733Z"
          />
        </svg>
      ),
    },
  ],
}

const HotelSection = () => {
  return (
    <section className="mx-auto flex w-full max-w-screen-lg flex-col items-center justify-between gap-8 px-5 pb-0 sm:pb-24 md:flex-row lg:gap-14">
      <div>
        <Link
          href="https://book.passkey.com/event/50932723/owner/1422/home"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex flex-col items-center justify-center before:absolute before:-bottom-1.5 before:h-3 before:w-3 before:rotate-45 before:border-b before:border-r before:border-[#313646] before:bg-[#1E212C] before:content-[''] md:before:-right-1.5 md:before:top-10 md:before:border-b-0 md:before:border-r md:before:border-t md:before:border-[#313646] md:before:bg-[#1E212C]"
        >
          <div className="flex items-center justify-center rounded-t border border-[#313646] bg-[#1E212C]">
            <div className="flex items-center justify-center overflow-hidden rounded-tl">
              <Image
                src="/assets/conf/hilton-salt-lake-city-center.jpg"
                alt="Hilton Salt Lake City Center"
                width={180}
                height={180}
                priority
                className="transition duration-300 ease-in-out group-hover:scale-105"
              />
            </div>
            <div className="px-4 py-2 pr-5 sm:px-5 sm:py-5 sm:pr-7">
              <h3 className="text-lg font-semibold leading-tight sm:text-xl sm:leading-tight">
                Hilton Salt Lake City Center
              </h3>
              <div className="mt-3 inline-flex text-sm">
                255 S W Temple St
                <br />
                Salt Lake City, UT 84101
              </div>
            </div>
          </div>
        </Link>
      </div>
      <div className="w-full max-w-md text-center md:text-left lg:max-w-lg">
        <h3 className="text-2xl font-semibold sm:text-3xl">
          Looking for a place to stay?
        </h3>
        <div className="mt-3 text-[#D6DEFF] sm:text-lg">
          <span>
            We've partnered with Hilton Salt Lake City Center to offer you a
            discounted rate on available rooms. Visit{' '}
            <a
              href="https://book.passkey.com/event/50932723/owner/1422/home"
              className="underline"
            >
              here
            </a>{' '}
            to find your discounted room and book your stay today.
          </span>
        </div>
      </div>
    </section>
  )
}

const WebDevChallengeLocation = () => {
  return (
    <section
      id="web-dev-challenge-location"
      className="mx-auto flex w-full max-w-screen-lg flex-col items-center justify-between gap-8 px-5 pb-0 sm:pb-24 md:flex-row lg:gap-14"
    >
      <div>
        <Link
          href="https://www.saltlakebrewingco.com/squatters"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex flex-col items-center justify-center before:absolute before:-bottom-1.5 before:h-3 before:w-3 before:rotate-45 before:border-b before:border-r before:border-[#313646] before:bg-[#1E212C] before:content-[''] md:before:-right-1.5 md:before:top-10 md:before:border-b-0 md:before:border-r md:before:border-t md:before:border-[#313646] md:before:bg-[#1E212C]"
        >
          <div className="flex items-center justify-center rounded-t border border-[#313646] bg-[#1E212C]">
            <div className="flex items-center justify-center overflow-hidden rounded-tl">
              <Image
                src="/assets/conf/2025/bar.jpg"
                alt="Squatters Pub Brewery"
                width={180}
                height={180}
                priority
                className="transition duration-300 ease-in-out group-hover:scale-105"
              />
            </div>
            <div className="px-4 py-2 pr-5 sm:px-5 sm:py-5 sm:pr-7">
              <h3 className="text-lg font-semibold leading-tight sm:text-xl sm:leading-tight">
                Squatters Pub Brewery
              </h3>
              <div className="mt-3 inline-flex text-sm">
                147 W Broadway
                <br />
                Salt Lake City, UT 84101
              </div>
            </div>
          </div>
        </Link>
      </div>
      <div className="w-full max-w-md text-center md:text-left lg:max-w-lg">
        <h3 className="text-2xl font-semibold sm:text-3xl">
          Web Dev Challenge Location
        </h3>
        <div className="mt-3 text-[#D6DEFF] sm:text-lg">
          <span>
            The Web Dev Challenge will be held at Squatters Pub Brewery.
          </span>
        </div>
      </div>
    </section>
  )
}

type hardCodedSpeaker = {
  id: string
  fullName: string
  x?: string
  bluesky?: string
  profilePicture: string
}

const SpeakersList: React.FC<{
  speakers: Speaker[]
  showingSpeakerDetail: boolean | Speaker
  setShowingSpeakerDetail: React.Dispatch<
    React.SetStateAction<boolean | Speaker>
  >
}> = ({speakers, showingSpeakerDetail, setShowingSpeakerDetail}) => {
  const [hoveredItem, setHoveredItem] = React.useState<Speaker | null>()
  const [isHovering, setIsHovering] = React.useState(false)

  const router = useRouter()

  return (
    <>
      <section
        id="speakers"
        aria-label="speakers"
        className="mx-auto flex w-full max-w-screen-lg flex-col items-center justify-center"
      >
        <h2 className="w-full px-5 pb-5 text-3xl font-bold sm:text-4xl">
          Speakers
        </h2>
        <div className="grid grid-cols-2 px-3 sm:grid-cols-3 sm:px-0 lg:grid-cols-4">
          {speakers.map((speaker) => {
            return (
              <Link
                key={speaker.id}
                href={`/conf/2025/${slugify(speaker.fullName)}`}
                onClick={() => {
                  track('clicked speaker', {
                    title: 'conf2025',
                    type: 'speaker',
                    location: speaker.fullName,
                  })
                }}
              >
                <motion.div
                  onMouseEnter={() => {
                    setHoveredItem(speaker)
                    setIsHovering(true)
                  }}
                  onMouseLeave={() => {
                    setIsHovering(false)
                  }}
                  className="relative flex flex-col items-center p-2 sm:p-6"
                  // tabIndex={0}
                  // aria-haspopup={!speaker?.video && 'dialog'}
                  // aria-expanded={showingSpeakerDetail ? 'true' : 'false'}
                >
                  <motion.div
                    animate={{
                      filter:
                        isHovering && hoveredItem?.id !== speaker.id
                          ? 'saturate(0.5)'
                          : 'saturate(1)',
                    }}
                    className="relative bg-gray-950"
                  >
                    {speaker.profilePicture && (
                      <Image
                        loading="eager"
                        className="rounded opacity-90"
                        src={speaker.profilePicture}
                        alt={speaker.fullName}
                        width={230}
                        height={230}
                      />
                    )}
                    {speaker?.video ? (
                      <motion.div
                        initial={{
                          opacity: 0,
                          scale: 0.9,
                        }}
                        animate={{
                          opacity:
                            isHovering && hoveredItem?.id === speaker.id
                              ? 1
                              : 0,
                          scale:
                            isHovering && hoveredItem?.id === speaker.id
                              ? 1
                              : 0.9,
                        }}
                        className="absolute bottom-3 right-3 flex items-center justify-center rounded-full bg-gray-900"
                      >
                        <PlayIcon className="h-5 w-5" />
                      </motion.div>
                    ) : null}
                  </motion.div>
                  <h3 className="w-full pt-4 text-lg font-semibold leading-tight">
                    {speaker.fullName}
                  </h3>
                  <h4 className="w-full pt-1 text-sm text-[#D6DEFF]">
                    {speaker.tagLine}
                  </h4>
                  <AnimatePresence mode="wait">
                    {isHovering && hoveredItem?.id !== speaker.id && (
                      <motion.div
                        initial={{
                          opacity: 0,
                        }}
                        exit={{opacity: 0}}
                        animate={{
                          opacity: [0, 0.4],
                        }}
                        className="absolute h-full w-full bg-foreground dark:bg-background"
                      />
                    )}
                  </AnimatePresence>
                </motion.div>
              </Link>
            )
          })}
        </div>
      </section>
      {/* <SpeakerDetail
        speakers={speakers}
        showingSpeakerDetail={showingSpeakerDetail}
        setShowingSpeakerDetail={setShowingSpeakerDetail}
      /> */}
    </>
  )
}
const sortRoomsByPriority = (rooms: any[]): any[] => {
  const roomPriority: any = {
    'Registration Desk': 1,
    // 'To be determined': 2,
    Wasatch: 3,
    Seminar: 4,
    'Grand Ballroom C': 5,
  }

  return [...rooms].sort((a, b) => roomPriority[a.name] - roomPriority[b.name])
}

export const Schedule: React.FC<{
  schedule: Schedule
  speakers: Speaker[]
  title?: string
}> = ({schedule, speakers, title = 'Schedule'}) => {
  return (
    <section
      id="schedule"
      aria-label="schedule"
      className="mx-auto w-full max-w-screen-lg sm:p-4"
    >
      <div className="item-scenter flex w-full flex-col justify-between gap-5 md:flex-row">
        <h2 className="px-4 pb-10 text-4xl font-bold sm:px-0 sm:text-5xl print:hidden">
          {title}
        </h2>
        <button
          type="button"
          className="hidden h-10 items-center justify-center whitespace-nowrap rounded-md border border-white/10 bg-foreground px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:border-input dark:bg-background dark:hover:bg-white/5 md:inline-flex print:hidden"
          onClick={() => {
            const scheduleRoute = window.open('/conf/schedule', '_blank')
            scheduleRoute?.print()
          }}
        >
          Save as PDF
        </button>
      </div>
      {schedule.map((day) => (
        <div key={day.date} className="mb-8">
          <h2 className="mb-4 px-4 text-2xl font-bold sm:px-0 print:text-black">
            {format(parseISO(day.date), 'EEEE, MM/dd/yyyy')}
          </h2>
          {sortRoomsByPriority(day.rooms).map((room: Room) => (
            <div key={room.id} className="mb-6">
              <h3 className="mb-2 px-4 text-lg font-semibold sm:px-0 print:text-black">
                Room: {room.name}
              </h3>
              <Accordion type="multiple" className="w-full">
                <ul className="flex flex-col divide-y divide-white/10">
                  {room.sessions.map((session: Session) => {
                    const hasMultipleSpeakers = session?.speakers?.length > 1
                    const speaker = session?.speakers[0]?.name
                    const talk = session?.talk

                    const Speaker: React.FC<{
                      className?: string
                      speaker: string
                    }> = ({className, speaker}) => {
                      return (
                        <div className={cn('items-center gap-2', className)}>
                          {getProfilePictureForWorkshopInstructor(
                            speaker,
                            speakers,
                          ) && (
                            <Image
                              alt=""
                              aria-hidden="true"
                              src={getProfilePictureForWorkshopInstructor(
                                speaker,
                                speakers,
                              )}
                              width={40}
                              height={40}
                              className="w-8 rounded-full md:w-auto"
                            />
                          )}
                          <span className="text-sm leading-tight">
                            {speaker}
                          </span>
                        </div>
                      )
                    }

                    const AccordionTriggerComp = talk
                      ? 'div'
                      : session.description
                      ? AccordionTrigger
                      : 'div'

                    return (
                      <AccordionItem
                        value={session.id}
                        key={session.id}
                        className="border-b-0"
                        asChild
                      >
                        <li>
                          <AccordionTriggerComp
                            className={cn(
                              'w-full px-2 [&_[data-chevron]]:print:hidden',
                              {
                                'transition hover:bg-white/5':
                                  session.description,
                                'pr-6': !session.description,
                              },
                            )}
                          >
                            <div
                              className={cn(
                                'md:group flex w-full items-start gap-3 py-2 md:items-center',
                                {
                                  '': session.title === 'Break',
                                },
                              )}
                            >
                              <div className="flex w-full max-w-[80px] items-center pt-2 text-[#D6DEFF] md:max-w-[160px] md:pt-0 print:text-black">
                                <p className="whitespace-nowrap text-left text-xs font-semibold tabular-nums leading-none md:text-sm md:font-medium">
                                  {format(
                                    parseISO(session.startsAt),
                                    'hh:mm a',
                                  )}{' '}
                                  {'–'} <br className="block md:hidden" />
                                  {format(
                                    parseISO(session.endsAt),

                                    'hh:mm a',
                                  )}
                                </p>
                              </div>
                              <div className="col-span-4 w-full md:col-span-3">
                                <div className="flex w-full flex-col items-start gap-2 text-left md:flex-row md:items-center">
                                  <h4 className="w-full font-semibold leading-tight sm:text-lg print:text-black">
                                    {session.title}
                                  </h4>
                                  {talk &&
                                    !session.title.includes(
                                      'OPTIONAL WORKSHOP REQUIRES A SEPARATE TICKET',
                                    ) && (
                                      <Button
                                        asChild
                                        size="sm"
                                        variant="secondary"
                                        className="inline-flex font-semibold"
                                      >
                                        <Link href={`/talks/${talk.slug}`}>
                                          Watch{' '}
                                          <Icon
                                            name="Playmark"
                                            className="ml-2 h-3 w-3"
                                          />
                                        </Link>
                                      </Button>
                                    )}
                                  {hasMultipleSpeakers ? (
                                    <div className="flex w-full items-center gap-2 md:hidden">
                                      {session?.speakers?.map((speaker) => {
                                        return (
                                          <Speaker
                                            key={speaker.name}
                                            speaker={speaker.name}
                                            className="mt-2 flex w-full max-w-none md:hidden print:text-black"
                                          />
                                        )
                                      })}
                                    </div>
                                  ) : (
                                    <>
                                      {speaker && (
                                        <Speaker
                                          speaker={speaker}
                                          className="mt-2 flex md:hidden print:text-black"
                                        />
                                      )}
                                    </>
                                  )}
                                </div>
                                {/* <p className="text-sm">{session.description}</p> */}
                              </div>
                              {hasMultipleSpeakers ? (
                                <div className="hidden w-full items-center gap-2 md:flex">
                                  {session?.speakers?.map((speaker) => {
                                    return (
                                      <Speaker
                                        key={speaker.name}
                                        speaker={speaker.name}
                                        className="hidden w-full max-w-[200px] md:flex"
                                      />
                                    )
                                  })}
                                </div>
                              ) : (
                                <>
                                  {speaker && (
                                    <Speaker
                                      speaker={speaker}
                                      className="hidden w-full max-w-[200px] md:flex"
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          </AccordionTriggerComp>
                          <AccordionContent>
                            <p className="px-4 pb-5 text-left text-sm leading-relaxed text-[#D6DEFF] md:ml-[160px] md:px-5 md:text-base print:text-black">
                              {session.description}
                            </p>
                          </AccordionContent>
                        </li>
                      </AccordionItem>
                    )
                  })}
                </ul>
              </Accordion>
            </div>
          ))}
        </div>
      ))}
    </section>
  )
}
